import { Fragment, type MouseEvent, type ReactNode, useEffect, useState } from "react";
import type { PremiumReport } from "../../shared/types";
import { AdminMetricCard } from "../components/admin/AdminMetricCard";
import { ReportView } from "../components/report/ReportView";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { Section } from "../components/ui/Section";

const ADMIN_TOKEN_STORAGE_KEY = "trybraintest_admin_token";

type AuthState = "idle" | "checking" | "authorized" | "unauthorized";
type AdminSection = "home" | "tests" | "orders" | "emails" | "usage";
type DeviceType = "Mobile" | "Tablet" | "Desktop" | "Unknown";

type AdminRecentAttempt = {
  attemptId: string;
  shortId: string;
  startedAt: string;
  completedAt: string | null;
  createdAt: string;
  status: string;
  ageBand: string | null;
  currentIndex: number;
  totalScoredQuestions: number;
  answeredCount: number;
  progressLabel: string;
  totalTimeMs: number | null;
  accuracyPercent: number | null;
  performanceBand: string | null;
};

type AdminAttemptListItem = AdminRecentAttempt & {
  createdAt: string;
  adminLabel?: string | null;
  email?: string;
  orderId?: string;
  orderStatus?: string;
  reportId?: string;
  reportStatus?: string;
  deviceType: DeviceType;
  deviceSummary: string | null;
  paidReport: boolean;
};

type AttemptStatusFilter = "all" | "completed" | "incomplete";
type AttemptPaymentFilter = "all" | "completed_paid" | "completed_unpaid";
type AttemptRangeFilter = "lifetime" | "today" | "last7d" | "month" | "custom";

type AdminAttemptsFilters = {
  status: AttemptStatusFilter;
  payment: AttemptPaymentFilter;
  range: AttemptRangeFilter;
  from: string;
  to: string;
  search: string;
  page: number;
  pageSize: number;
};

type AdminAttemptsResponse = {
  items: AdminAttemptListItem[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  filters: {
    status: string;
    payment: string;
    range: string;
    from?: string;
    to?: string;
    search?: string;
  };
};

type AdminReportPreviewResponse = {
  report: PremiumReport;
  source: "stored" | "generated_preview";
};

type AdminReportPreviewState = AdminReportPreviewResponse & {
  attemptId: string;
  attemptShortId: string;
};

type AdminContactEntry = {
  id: string;
  createdAt: string;
  status: string;
  kind: "contact" | "progress_reminder" | "unknown";
  email: string;
  subject: string;
  messagePreview: string;
  source?: string;
};

type AdminRecentOrder = {
  orderId: string;
  shortId: string;
  createdAt: string;
  paidAt?: string;
  status: string;
  amountCents: number;
  amountFormatted: string;
  currency: string;
  email?: string;
  attemptShortId?: string;
  stripeSessionShortId?: string;
  reportId?: string;
  reportStatus?: string;
};

type AdminOrderChildItem = {
  orderId: string;
  shortId: string;
  createdAt: string;
  updatedAt: string | null;
  paidAt: string | null;
  status: string;
  amountCents: number;
  amountFormatted: string;
  currency: string;
  stripeSessionShortId: string | null;
  checkoutDeviceType: DeviceType;
  checkoutDeviceSummary: string | null;
};

type AdminOrderListItem = {
  attemptId: string;
  attemptShortId: string;
  email: string | null;
  groupStatus: "paid" | "pending" | "failed" | "expired" | "unknown";
  latestOrderAt: string;
  firstOrderAt: string;
  paymentAttempts: number;
  pendingAttempts: number;
  paidAttempts: number;
  failedAttempts: number;
  expiredAttempts: number;
  paidAmountCents: number | null;
  paidAmountFormatted: string | null;
  currency: string | null;
  reportId: string | null;
  reportStatus: string | null;
  emailStatus: string | null;
  latestStripeSessionShortId: string | null;
  checkoutDeviceType: DeviceType;
  checkoutDeviceSummary: string | null;
  reportOpenCount: number;
  pdfDownloadCount: number;
  orders: AdminOrderChildItem[];
};

type OrderStatusFilter = "all" | "paid" | "pending" | "failed" | "expired";
type OrderReportFilter = "all" | "with_report" | "without_report";
type OrderEmailFilter = "all" | "email_sent" | "email_failed" | "email_missing";
type OrderRangeFilter = "lifetime" | "today" | "last7d" | "month" | "custom";

type AdminOrdersFilters = {
  status: OrderStatusFilter;
  report: OrderReportFilter;
  email: OrderEmailFilter;
  range: OrderRangeFilter;
  from: string;
  to: string;
  search: string;
  page: number;
  pageSize: number;
};

type AdminOrdersResponse = {
  items: AdminOrderListItem[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  filters: {
    status: string;
    report: string;
    email: string;
    range: string;
    from?: string;
    to?: string;
    search?: string;
  };
};

type AdminEmailType = "contact" | "progress_reminder" | "report_delivery" | "unknown";

type AdminEmailListItem = {
  id: string;
  createdAt: string;
  type: AdminEmailType;
  source: string | null;
  name?: string | null;
  email: string;
  subject: string | null;
  status: string;
  paidCustomer: boolean | null;
  preview: string;
  fullMessage?: string | null;
  provider?: string | null;
  providerMessageId?: string | null;
  reportId?: string | null;
  orderId?: string | null;
};

type EmailTypeFilter = "all" | "contact" | "progress_reminder" | "report_delivery";
type EmailStatusFilter = "all" | "new" | "sent" | "failed" | "skipped";
type EmailPaidFilter = "all" | "paid_customer" | "non_paid_customer" | "unknown";
type EmailRangeFilter = "lifetime" | "today" | "last7d" | "month" | "custom";

type AdminEmailsFilters = {
  type: EmailTypeFilter;
  status: EmailStatusFilter;
  paid: EmailPaidFilter;
  range: EmailRangeFilter;
  from: string;
  to: string;
  search: string;
  page: number;
  pageSize: number;
};

type AdminEmailsResponse = {
  items: AdminEmailListItem[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  filters: {
    type: string;
    status: string;
    paid: string;
    range: string;
    from?: string;
    to?: string;
    search?: string;
  };
};

type AdminReportEmailSource = "admin_manual_report_delivery" | "admin_contact_resend";

type AdminMetricsResponse = {
  overview: {
    totalAttempts: number;
    completedTests: number;
    completionRatePercent: number;
    averageCompletionTimeMs: number | null;
    averageAccuracyPercent: number | null;
    startedToday: number;
    completedToday: number;
  };
  breakdowns: {
    statusCounts: Array<{ status: string; count: number }>;
    ageBandCounts: Array<{ ageBand: string | null; count: number }>;
  };
  recentAttempts: AdminRecentAttempt[];
  contact: {
    contactMessageCount: number;
    reminderSignupCount: number;
    recentEntries: AdminContactEntry[];
  };
  sales: {
    paidReports: number;
    grossRevenueCents: number;
    grossRevenueFormatted: string;
    pendingOrders: number;
    paidConversionRatePercent: number | null;
    refunds: "Coming later";
    statusCounts: Array<{ status: string; count: number }>;
    reportStatusCounts: Array<{ status: string; count: number }>;
    recentOrders: AdminRecentOrder[];
  };
  futurePlaceholders: {
    refunds: string;
    emailDelivery: string;
  };
};

type AdminUsageResponse = {
  counts: {
    sessions: number;
    analyticsSessions: number;
    analyticsEvents: number;
    attempts: number;
    attemptItems: number;
    orders: number;
    reports: number;
    contactMessages: number;
    emailLogs: number;
    stripeEvents: number;
    questionItems: number;
    questionStats: number;
    adminEvents: number;
  };
  health: {
    pendingOrders: number;
    expiredOrders: number;
    failedOrders: number;
    latestCompletedAttemptAt: string | null;
    latestPaidOrderAt: string | null;
    latestReportUnlockedAt: string | null;
    latestStripeEventAt: string | null;
    latestStripeEventStatus: string | null;
    latestStripeEventType: string | null;
    latestEmailSentAt: string | null;
    latestEmailFailedAt: string | null;
  };
  config: {
    stripeConfigured: boolean;
    resendConfigured: boolean;
    reportTokenConfigured: boolean;
    adminTokenConfigured: boolean;
    publicSiteUrlConfigured: boolean;
    cloudflareConfigured?: boolean;
  };
  cloudflare: {
    configured: boolean;
    status: "configured" | "missing_config" | "error";
    errorMessage?: string | null;
    d1: {
      storageBytes: number | null;
      storageLimitBytes: number | null;
      storageUsedPercent: number | null;
      storageRemainingBytes: number | null;
      rowsReadToday: number | null;
      rowsReadDailyLimit: number | null;
      rowsReadUsedPercent: number | null;
      rowsWrittenToday: number | null;
      rowsWrittenDailyLimit: number | null;
      rowsWrittenUsedPercent: number | null;
    };
  };
  databaseEstimate: {
    estimatedTotalBytes: number;
    byTable: Array<{
      table: string;
      label: string;
      rows: number;
      estimatedBytes: number;
      estimatedPercentOfEstimate: number;
    }>;
  };
  resendUsage: {
    sentToday: number;
    failedToday: number;
    skippedToday: number;
    sentThisMonth: number;
    failedThisMonth: number;
    skippedThisMonth: number;
    dailyLimit: number;
    monthlyLimit: number;
    usedPercentToday: number;
    usedPercentMonth: number;
  };
  notes: string[];
};

type AdminLiveAnalyticsResponse = {
  activeNow: number;
  windowMinutes: number;
  sessions: Array<{
    sessionId: string;
    shortId: string;
    currentPath: string | null;
    firstSeenAt: string;
    lastSeenAt: string;
    deviceType: DeviceType;
    userAgentSummary: string | null;
  }>;
};

type AdminAnalyticsRangeKey = "today" | "yesterday" | "last7d" | "month";

type AdminAnalyticsOverviewResponse = {
  range: {
    key: AdminAnalyticsRangeKey;
    from: string;
    to: string;
    bucket: "hour" | "day";
  };
  totals: {
    sessions: number;
    pageViews: number;
    totalOrders: number;
    totalSalesCents: number;
    totalSalesFormatted: string;
    conversionRatePercent: number | null;
    testStarts: number;
    completedTests: number;
    checkoutStarts: number;
    paymentCompleted: number;
    paidReports: number;
    reportOpens: number;
    contactSubmissions: number;
    reminderSignups: number;
    sessionToStartPercent: number | null;
    startToCompletePercent: number | null;
    completeToCheckoutPercent: number | null;
    checkoutToPaidPercent: number | null;
    paidToReportOpenPercent: number | null;
  };
  series: Array<{
    bucketStart: string;
    label: string;
    sessions: number;
    pageViews: number;
    totalOrders: number;
    salesCents: number;
    testStarts: number;
    completedTests: number;
    checkoutStarts: number;
    paymentCompleted: number;
    paidReports: number;
    reportOpens: number;
    contactSubmissions: number;
    reminderSignups: number;
  }>;
};

type AdminAnalyticsSessionDetailsResponse = {
  range: {
    key: AdminAnalyticsRangeKey;
    from: string;
    to: string;
  };
  generatedAt: string;
  groups: Array<{
    id: string;
    sessionId: string;
    visitorLabel: string;
    country: string;
    source: string;
    countryCode: string | null;
    referrerHost: string | null;
    utmSource: string | null;
    utmMedium: string | null;
    utmCampaign: string | null;
    landingPath: string | null;
    device: DeviceType;
    deviceSummary: string | null;
    firstSeenAt: string;
    lastSeenAt: string;
    pageViews: number;
    testStarts: number;
    completedTests: number;
    checkoutStarts: number;
    paidReports: number;
    pendingPayments: number;
    reportOpens: number;
    latestPath: string | null;
    details: Array<{
      time: string;
      type: string;
      label: string;
      path: string | null;
      attemptId: string | null;
      orderId: string | null;
      reportId: string | null;
    }>;
  }>;
};

async function fetchAdminMetrics(token: string): Promise<AdminMetricsResponse> {
  const response = await fetch("/api/admin/metrics", {
    headers: {
      "x-admin-token": token,
    },
  });

  if (!response.ok) {
    throw new Error(response.status === 401 || response.status === 403 ? "unauthorized" : "request_failed");
  }

  return response.json() as Promise<AdminMetricsResponse>;
}

async function fetchAdminUsage(token: string): Promise<AdminUsageResponse> {
  const response = await fetch("/api/admin/usage", {
    headers: {
      "x-admin-token": token,
    },
  });

  if (!response.ok) {
    throw new Error(response.status === 401 || response.status === 403 ? "unauthorized" : "request_failed");
  }

  return response.json() as Promise<AdminUsageResponse>;
}

async function fetchAdminLiveAnalytics(token: string): Promise<AdminLiveAnalyticsResponse> {
  const response = await fetch("/api/admin/analytics/live", {
    headers: {
      "x-admin-token": token,
    },
  });

  if (!response.ok) {
    throw new Error(response.status === 401 || response.status === 403 ? "unauthorized" : "request_failed");
  }

  return response.json() as Promise<AdminLiveAnalyticsResponse>;
}

async function fetchAdminAnalyticsOverview(token: string, range: AdminAnalyticsRangeKey): Promise<AdminAnalyticsOverviewResponse> {
  const response = await fetch(`/api/admin/analytics/overview?range=${encodeURIComponent(range)}`, {
    headers: {
      "x-admin-token": token,
    },
  });

  if (!response.ok) {
    throw new Error(response.status === 401 || response.status === 403 ? "unauthorized" : "request_failed");
  }

  return response.json() as Promise<AdminAnalyticsOverviewResponse>;
}

async function fetchAdminAnalyticsSessionDetails(token: string, range: AdminAnalyticsRangeKey): Promise<AdminAnalyticsSessionDetailsResponse> {
  const response = await fetch(`/api/admin/analytics/session-details?range=${encodeURIComponent(range)}`, {
    headers: {
      "x-admin-token": token,
    },
  });

  if (!response.ok) {
    throw new Error(response.status === 401 || response.status === 403 ? "unauthorized" : "request_failed");
  }

  return response.json() as Promise<AdminAnalyticsSessionDetailsResponse>;
}

async function fetchAdminAttempts(token: string, filters: AdminAttemptsFilters): Promise<AdminAttemptsResponse> {
  const params = new URLSearchParams({
    status: filters.status,
    payment: filters.payment,
    range: filters.range,
    page: String(filters.page),
    pageSize: String(filters.pageSize),
  });

  if (filters.search.trim()) params.set("search", filters.search.trim());
  if (filters.range === "custom") {
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
  }

  const response = await fetch(`/api/admin/attempts?${params.toString()}`, {
    headers: {
      "x-admin-token": token,
    },
  });

  if (!response.ok) {
    throw new Error(response.status === 401 || response.status === 403 ? "unauthorized" : "request_failed");
  }

  return response.json() as Promise<AdminAttemptsResponse>;
}

async function fetchAdminReportPreview(token: string, attemptId: string): Promise<AdminReportPreviewResponse> {
  const response = await fetch(`/api/admin/attempts/${encodeURIComponent(attemptId)}/report-preview`, {
    headers: {
      "x-admin-token": token,
    },
  });

  if (!response.ok) {
    throw new Error(response.status === 401 || response.status === 403 ? "unauthorized" : "request_failed");
  }

  return response.json() as Promise<AdminReportPreviewResponse>;
}

async function fetchAdminReportPreviewPdf(token: string, attemptId: string): Promise<Blob> {
  const response = await fetch(`/api/admin/attempts/${encodeURIComponent(attemptId)}/report-preview/pdf`, {
    headers: {
      "x-admin-token": token,
    },
  });

  if (!response.ok) {
    throw new Error(response.status === 401 || response.status === 403 ? "unauthorized" : "request_failed");
  }

  return response.blob();
}

function safeAdminPdfFilename(id: string) {
  const safeId = id.replace(/[^a-zA-Z0-9_-]/g, "-").slice(0, 80) || "preview";
  return `TryBrainTest-Report-Preview-${safeId}.pdf`;
}

async function updateAdminAttemptLabel(token: string, attemptId: string, label: string | null): Promise<{ ok: true; adminLabel: string | null }> {
  const response = await fetch(`/api/admin/attempts/${encodeURIComponent(attemptId)}/label`, {
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      "x-admin-token": token,
    },
    body: JSON.stringify({ label }),
  });

  if (!response.ok) {
    throw new Error(response.status === 401 || response.status === 403 ? "unauthorized" : "request_failed");
  }

  return response.json() as Promise<{ ok: true; adminLabel: string | null }>;
}

async function fetchAdminOrders(token: string, filters: AdminOrdersFilters): Promise<AdminOrdersResponse> {
  const params = new URLSearchParams({
    status: filters.status,
    report: filters.report,
    email: filters.email,
    range: filters.range,
    page: String(filters.page),
    pageSize: String(filters.pageSize),
  });

  if (filters.search.trim()) params.set("search", filters.search.trim());
  if (filters.range === "custom") {
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
  }

  const response = await fetch(`/api/admin/order-groups?${params.toString()}`, {
    headers: {
      "x-admin-token": token,
    },
  });

  if (!response.ok) {
    throw new Error(response.status === 401 || response.status === 403 ? "unauthorized" : "request_failed");
  }

  return response.json() as Promise<AdminOrdersResponse>;
}

async function fetchAdminEmails(token: string, filters: AdminEmailsFilters): Promise<AdminEmailsResponse> {
  const params = new URLSearchParams({
    type: filters.type,
    status: filters.status,
    paid: filters.paid,
    range: filters.range,
    page: String(filters.page),
    pageSize: String(filters.pageSize),
  });

  if (filters.search.trim()) params.set("search", filters.search.trim());
  if (filters.range === "custom") {
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
  }

  const response = await fetch(`/api/admin/emails?${params.toString()}`, {
    headers: {
      "x-admin-token": token,
    },
  });

  if (!response.ok) {
    throw new Error(response.status === 401 || response.status === 403 ? "unauthorized" : "request_failed");
  }

  return response.json() as Promise<AdminEmailsResponse>;
}

async function sendAdminReportDeliveryEmail(
  token: string,
  input: {
    email: string;
    reportOrAttemptId: string;
    source: AdminReportEmailSource;
    contactId?: string;
  }
): Promise<{ ok: true; status: "sent" | "failed" | "skipped" }> {
  const response = await fetch("/api/admin/reports/send-delivery-email", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-admin-token": token,
    },
    body: JSON.stringify(input),
  });
  const data = (await response.json().catch(() => null)) as { error?: { message?: string } } | { ok: true; status: "sent" | "failed" | "skipped" } | null;
  if (!response.ok) {
    throw new Error(data && "error" in data && data.error?.message ? data.error.message : "Could not send report email.");
  }
  return data as { ok: true; status: "sent" | "failed" | "skipped" };
}

export function AdminDashboard() {
  const [tokenInput, setTokenInput] = useState("");
  const [authState, setAuthState] = useState<AuthState>("idle");
  const [activeSection, setActiveSection] = useState<AdminSection>("home");
  const [metrics, setMetrics] = useState<AdminMetricsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const savedToken = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!savedToken) {
      return;
    }

    setAuthState("checking");
    fetchAdminMetrics(savedToken)
      .then((data) => {
        setMetrics(data);
        setAuthState("authorized");
        setError(null);
      })
      .catch(() => {
        sessionStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
        setAuthState("unauthorized");
        setError("Invalid admin access key.");
      });
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const token = tokenInput.trim();
    if (!token) {
      setAuthState("unauthorized");
      setError("Enter the admin access key.");
      return;
    }

    setAuthState("checking");
    setError(null);

    try {
      const data = await fetchAdminMetrics(token);
      sessionStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, token);
      setMetrics(data);
      setAuthState("authorized");
      setTokenInput("");
    } catch {
      sessionStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
      setMetrics(null);
      setAuthState("unauthorized");
      setError("Invalid admin access key.");
    }
  }

  function handleClearAccess() {
    sessionStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
    setMetrics(null);
    setAuthState("idle");
    setActiveSection("home");
    setError(null);
  }

  if (authState !== "authorized" || !metrics) {
    return (
      <Section className="min-h-[72vh]">
        <div className="mx-auto max-w-md">
          <Card>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-200">TryBrainTest Admin</p>
            <h1 className="mt-3 text-3xl font-bold text-white">Admin access</h1>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              Enter the protected internal access key to view dashboard metrics.
            </p>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <label className="block text-sm font-semibold text-slate-200" htmlFor="admin-token">
                Admin access key
              </label>
              <Input
                id="admin-token"
                autoComplete="off"
                type="password"
                value={tokenInput}
                onChange={(event) => setTokenInput(event.target.value)}
                placeholder="Enter access key"
              />
              {error ? <p className="text-sm text-rose-300">{error}</p> : null}
              <Button className="w-full" disabled={authState === "checking"} type="submit">
                {authState === "checking" ? "Checking..." : "Open dashboard"}
              </Button>
            </form>
          </Card>
        </div>
      </Section>
    );
  }

  return (
    <section className="w-full px-3 py-4 sm:px-5 lg:px-0 lg:py-0">
      <div className="w-full lg:min-h-screen">
        <AdminSidebar activeSection={activeSection} onSelect={setActiveSection} />

        <main className="min-w-0 space-y-5 lg:ml-[280px] lg:px-6 lg:py-6 xl:px-8">
          <AdminHeader onClearAccess={handleClearAccess} />
          {activeSection === "home" ? <HomeSection metrics={metrics} /> : null}
          {activeSection === "tests" ? <TestsSection metrics={metrics} /> : null}
          {activeSection === "orders" ? <OrdersSection metrics={metrics} /> : null}
          {activeSection === "emails" ? <EmailsSection metrics={metrics} /> : null}
          {activeSection === "usage" ? <UsageSection /> : null}
        </main>
      </div>
    </section>
  );
}

function AdminHeader({ onClearAccess }: { onClearAccess: () => void }) {
  return (
    <Card className="flex flex-col gap-4 border-cyan-300/15 bg-slate-950/70 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-cyan-200">Protected internal dashboard</p>
        <h1 className="mt-2 text-3xl font-bold text-white">TryBrainTest Admin</h1>
      </div>
      <Button className="sm:w-auto" variant="secondary" onClick={onClearAccess}>
        Clear access
      </Button>
    </Card>
  );
}

function AdminSidebar({
  activeSection,
  onSelect,
}: {
  activeSection: AdminSection;
  onSelect: (section: AdminSection) => void;
}) {
  const items: Array<{ id: AdminSection; label: string; helper: string }> = [
    { id: "home", label: "Home", helper: "Overview" },
    { id: "tests", label: "Tests", helper: "Attempts" },
    { id: "orders", label: "Orders", helper: "Payments" },
    { id: "emails", label: "Emails", helper: "Contact" },
    { id: "usage", label: "Usage", helper: "System" },
  ];

  return (
    <aside className="mb-5 lg:fixed lg:left-0 lg:top-0 lg:z-20 lg:mb-0 lg:h-screen lg:w-[280px] lg:overflow-y-auto lg:border-r lg:border-white/10 lg:bg-slate-950/55 lg:p-4">
      <Card className="border-white/10 bg-slate-950/75 p-3 lg:h-full lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0">
        <div className="px-2 py-2">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Admin</p>
          <p className="mt-1 text-lg font-bold text-white">TryBrainTest</p>
        </div>
        <nav className="mt-3 flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0" aria-label="Admin sections">
          {items.map((item) => {
            const isActive = item.id === activeSection;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onSelect(item.id)}
                className={[
                  "min-w-[140px] rounded-xl border px-3 py-3 text-left transition lg:min-w-0",
                  isActive
                    ? "border-cyan-300/40 bg-cyan-300/12 text-white shadow-[0_12px_30px_rgb(14_165_233_/_0.12)]"
                    : "border-transparent text-slate-300 hover:border-white/10 hover:bg-white/[0.04] hover:text-white",
                ].join(" ")}
              >
                <span className="block text-sm font-semibold">{item.label}</span>
                <span className="mt-1 block text-xs text-slate-400">{item.helper}</span>
              </button>
            );
          })}
        </nav>
      </Card>
    </aside>
  );
}

function HomeSection({ metrics }: { metrics: AdminMetricsResponse }) {
  const [liveAnalytics, setLiveAnalytics] = useState<AdminLiveAnalyticsResponse | null>(null);
  const [liveAnalyticsError, setLiveAnalyticsError] = useState<string | null>(null);
  const [analyticsRange, setAnalyticsRange] = useState<AdminAnalyticsRangeKey>("today");
  const [analyticsOverview, setAnalyticsOverview] = useState<AdminAnalyticsOverviewResponse | null>(null);
  const [analyticsOverviewError, setAnalyticsOverviewError] = useState<string | null>(null);
  const [analyticsOverviewLoading, setAnalyticsOverviewLoading] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!token) return;

    let cancelled = false;
    const loadLiveAnalytics = () => {
      fetchAdminLiveAnalytics(token)
        .then((data) => {
          if (cancelled) return;
          setLiveAnalytics(data);
          setLiveAnalyticsError(null);
        })
        .catch(() => {
          if (cancelled) return;
          setLiveAnalyticsError("Could not load live users.");
        });
    };

    loadLiveAnalytics();
    const intervalId = window.setInterval(loadLiveAnalytics, 45_000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!token) return;

    let cancelled = false;
    setAnalyticsOverviewLoading(true);
    fetchAdminAnalyticsOverview(token, analyticsRange)
      .then((data) => {
        if (cancelled) return;
        setAnalyticsOverview(data);
        setAnalyticsOverviewError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setAnalyticsOverviewError("Could not load analytics overview.");
      })
      .finally(() => {
        if (cancelled) return;
        setAnalyticsOverviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [analyticsRange]);

  return (
    <div className="space-y-5">
      <SectionHeader
        eyebrow="Home"
        title="Overview"
        description="High-level test, contact, and paid report activity from the protected admin metrics API."
      />
      <KpiGrid metrics={metrics} />

      <AnalyticsOverviewPanel
        data={analyticsOverview}
        error={analyticsOverviewError}
        isLoading={analyticsOverviewLoading}
        range={analyticsRange}
        onRangeChange={setAnalyticsRange}
      />

      <LiveUsersCard data={liveAnalytics} error={liveAnalyticsError} />

      <TrafficSessionDetailsPanel />

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        <BreakdownList title="Test status" items={metrics.breakdowns.statusCounts} labelKey="status" />
        <BreakdownList title="Age bands" items={metrics.breakdowns.ageBandCounts} labelKey="ageBand" emptyLabel="Unknown" />
        <BreakdownList title="Order status" items={metrics.sales.statusCounts} labelKey="status" />
        <BreakdownList title="Report status" items={metrics.sales.reportStatusCounts} labelKey="status" />
      </div>
    </div>
  );
}

function AnalyticsOverviewPanel({
  data,
  error,
  isLoading,
  range,
  onRangeChange,
}: {
  data: AdminAnalyticsOverviewResponse | null;
  error: string | null;
  isLoading: boolean;
  range: AdminAnalyticsRangeKey;
  onRangeChange: (range: AdminAnalyticsRangeKey) => void;
}) {
  const rangeOptions: Array<{ value: AdminAnalyticsRangeKey; label: string }> = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last7d", label: "Last 7 days" },
    { value: "month", label: "This month" },
  ];

  const totals = data?.totals;
  const cards: Array<{ label: string; value: string }> = [
    { label: "Sessions", value: totals ? totals.sessions.toLocaleString("el-GR") : "..." },
    { label: "Page views", value: totals ? totals.pageViews.toLocaleString("el-GR") : "..." },
    { label: "Total sales", value: totals ? totals.totalSalesFormatted : "..." },
    { label: "Total orders", value: totals ? totals.totalOrders.toLocaleString("el-GR") : "..." },
    { label: "Conversion rate", value: totals ? formatPercent(totals.conversionRatePercent) : "..." },
  ];

  return (
    <Card>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">Analytics overview</p>
          <h3 className="mt-2 text-2xl font-bold text-white">Traffic and sales trend</h3>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-400">
            Aggregate public sessions, page views, paid orders, and sales for the selected range.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 rounded-2xl border border-white/10 bg-slate-950/60 p-1">
          {rangeOptions.map((option) => {
            const isActive = option.value === range;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => onRangeChange(option.value)}
                className={[
                  "rounded-xl px-3 py-2 text-sm font-semibold transition",
                  isActive ? "bg-cyan-300/15 text-cyan-100" : "text-slate-300 hover:bg-white/5 hover:text-white",
                ].join(" ")}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(160px,1fr))]">
        {cards.map((card) => (
          <div key={card.label} className="rounded-2xl border border-white/10 bg-white/[0.035] p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{card.label}</p>
            <p className="mt-2 text-2xl font-bold text-white">{card.value}</p>
          </div>
        ))}
      </div>

      {totals ? <AnalyticsFunnelStrip totals={totals} /> : null}

      <div className="mt-5 rounded-2xl border border-white/10 bg-slate-950/55 p-4">
        {error ? (
          <p className="text-sm text-rose-200">{error}</p>
        ) : isLoading && !data ? (
          <p className="text-sm text-slate-400">Loading analytics overview...</p>
        ) : data ? (
          <AnalyticsLineChart series={data.series} />
        ) : (
          <p className="text-sm text-slate-400">No analytics data available yet.</p>
        )}
      </div>
    </Card>
  );
}

function AnalyticsFunnelStrip({ totals }: { totals: AdminAnalyticsOverviewResponse["totals"] }) {
  const steps: Array<{ label: string; value: number; helper: string }> = [
    { label: "Sessions", value: totals.sessions, helper: "Public visitors" },
    { label: "Test starts", value: totals.testStarts, helper: `${formatPercent(totals.sessionToStartPercent)} from sessions` },
    { label: "Completed tests", value: totals.completedTests, helper: `${formatPercent(totals.startToCompletePercent)} from starts` },
    { label: "Checkout starts", value: totals.checkoutStarts, helper: `${formatPercent(totals.completeToCheckoutPercent)} from completed` },
    { label: "Paid reports", value: totals.paidReports, helper: `${formatPercent(totals.checkoutToPaidPercent)} from checkout` },
    { label: "Report opens", value: totals.reportOpens, helper: `${formatPercent(totals.paidToReportOpenPercent)} from paid` },
  ];

  return (
    <div className="mt-5 rounded-2xl border border-cyan-300/15 bg-cyan-300/[0.035] p-4">
      <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h4 className="text-lg font-semibold text-white">Funnel</h4>
          <p className="text-sm text-slate-400">Session to paid report flow for the selected range.</p>
        </div>
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Aggregate only</p>
      </div>
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
        {steps.map((step) => (
          <div key={step.label} className="rounded-xl border border-white/10 bg-slate-950/45 p-3">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{step.label}</p>
            <p className="mt-2 text-2xl font-bold text-white">{step.value.toLocaleString("el-GR")}</p>
            <p className="mt-1 text-xs text-slate-400">{step.helper}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalyticsLineChart({ series }: { series: AdminAnalyticsOverviewResponse["series"] }) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const chartWidth = 100;
  const chartHeight = 56;
  const paddingTop = 5;
  const paddingBottom = 9;
  const values = series.flatMap((point) => [point.pageViews, point.sessions]);
  const maxObservedValue = Math.max(0, ...values);
  const tickValues = buildCountTicks(maxObservedValue);
  const uniqueTicks = Array.from(new Set(tickValues)).sort((a, b) => b - a);
  const maxValue = Math.max(1, uniqueTicks[0] ?? maxObservedValue);
  const isEmpty = values.every((value) => value === 0);
  const xForIndex = (index: number) => (series.length <= 1 ? chartWidth / 2 : (index / (series.length - 1)) * chartWidth);
  const yForValue = (value: number) => {
    const usableHeight = chartHeight - paddingTop - paddingBottom;
    return paddingTop + usableHeight - (value / maxValue) * usableHeight;
  };
  const pointsFor = (field: "pageViews" | "sessions") =>
    series.map((point, index) => `${xForIndex(index).toFixed(2)},${yForValue(point[field]).toFixed(2)}`).join(" ");
  const isHourlySeries = isHourlyAnalyticsSeries(series);
  const labels = buildXAxisLabels(series, isHourlySeries);
  const hoverPoint = hoverIndex === null ? null : series[hoverIndex];
  const hoverX = hoverIndex === null ? null : xForIndex(hoverIndex);
  const hoverPageViewY = hoverPoint ? yForValue(hoverPoint.pageViews) : null;
  const hoverSessionY = hoverPoint ? yForValue(hoverPoint.sessions) : null;
  const tooltipStyle =
    hoverX !== null && hoverX > 68
      ? { right: `calc(${100 - hoverX}% + 14px)` }
      : { left: `calc(${hoverX ?? 0}% + 14px)` };

  function handleChartMouseMove(event: MouseEvent<HTMLDivElement>) {
    if (series.length === 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    setHoverIndex(Math.round(ratio * (series.length - 1)));
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-4 text-sm">
          <span className="inline-flex items-center gap-2 text-cyan-100">
            <span className="h-2.5 w-2.5 rounded-full bg-cyan-300" />
            Page views
          </span>
          <span className="inline-flex items-center gap-2 text-blue-100">
            <span className="h-2.5 w-2.5 rounded-full bg-blue-400" />
            Sessions
          </span>
        </div>
        {isEmpty ? <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-slate-300">No activity yet</span> : null}
      </div>

      <div className="grid h-72 w-full grid-cols-[3rem_1fr] gap-3 overflow-hidden">
        <div className="relative h-full text-xs text-slate-500">
          {uniqueTicks.map((tick) => (
            <span
              key={tick}
              className="absolute right-0 -translate-y-1/2 tabular-nums"
              style={{ top: `${(yForValue(tick) / chartHeight) * 100}%` }}
            >
              {tick.toLocaleString("el-GR")}
            </span>
          ))}
        </div>
        <div
          className="relative h-full cursor-crosshair overflow-hidden rounded-xl"
          onMouseMove={handleChartMouseMove}
          onMouseLeave={() => setHoverIndex(null)}
        >
          <svg className="block h-full w-full overflow-hidden" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none" role="img" aria-label="Analytics line chart">
            <defs>
              <clipPath id="admin-analytics-chart-clip">
                <rect x="0" y={paddingTop} width={chartWidth} height={chartHeight - paddingTop - paddingBottom} />
              </clipPath>
            </defs>
            <g clipPath="url(#admin-analytics-chart-clip)">
              {uniqueTicks.map((tick) => {
                const y = yForValue(tick);
                return <line key={tick} x1="0" x2={chartWidth} y1={y} y2={y} stroke="rgba(148, 163, 184, 0.16)" strokeWidth="0.35" />;
              })}
              <polyline fill="none" stroke="rgba(34, 211, 238, 0.95)" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" points={pointsFor("pageViews")} vectorEffect="non-scaling-stroke" />
              <polyline fill="none" stroke="rgba(96, 165, 250, 0.9)" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" points={pointsFor("sessions")} vectorEffect="non-scaling-stroke" />
              {hoverPoint && hoverX !== null ? (
                <>
                  <line x1={hoverX} x2={hoverX} y1={paddingTop} y2={chartHeight - paddingBottom} stroke="rgba(226, 232, 240, 0.34)" strokeDasharray="2 2" strokeWidth="0.45" vectorEffect="non-scaling-stroke" />
                </>
              ) : null}
            </g>
          </svg>
          {hoverPoint && hoverX !== null && hoverPageViewY !== null && hoverSessionY !== null ? (
            <>
              <span
                className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-slate-950 bg-cyan-300 shadow-[0_0_0_3px_rgb(34_211_238_/_0.16)]"
                style={{ left: `${hoverX}%`, top: `${(hoverPageViewY / chartHeight) * 100}%` }}
              />
              <span
                className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-slate-950 bg-blue-400 shadow-[0_0_0_3px_rgb(96_165_250_/_0.16)]"
                style={{ left: `${hoverX}%`, top: `${(hoverSessionY / chartHeight) * 100}%` }}
              />
            </>
          ) : null}
          {hoverPoint ? (
            <div
              className="pointer-events-none absolute top-3 z-10 min-w-44 rounded-xl border border-white/10 bg-slate-950/95 p-3 text-sm shadow-2xl shadow-black/30"
              style={tooltipStyle}
            >
              <p className="font-semibold text-white">{formatChartTooltipDate(hoverPoint.bucketStart, isHourlySeries)}</p>
              <div className="mt-2 space-y-1 text-xs">
                <p className="flex items-center justify-between gap-4 text-cyan-100">
                  <span>Page views</span>
                  <span className="font-semibold tabular-nums">{hoverPoint.pageViews.toLocaleString("el-GR")}</span>
                </p>
                <p className="flex items-center justify-between gap-4 text-blue-100">
                  <span>Sessions</span>
                  <span className="font-semibold tabular-nums">{hoverPoint.sessions.toLocaleString("el-GR")}</span>
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-3 ml-12 flex justify-between gap-3 text-xs text-slate-500">
        {labels.map(({ point, label }) => (
          <span key={point.bucketStart}>{label}</span>
        ))}
      </div>
    </div>
  );
}

function buildCountTicks(maxObservedValue: number) {
  const max = Math.ceil(maxObservedValue);
  if (max <= 0) return [0];
  if (max <= 10) {
    return Array.from({ length: max + 1 }, (_, index) => max - index);
  }

  const roughStep = max / 4;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const step = [1, 2, 5, 10].map((value) => value * magnitude).find((value) => value >= roughStep) ?? magnitude * 10;
  const ceiling = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let value = 0; value <= ceiling; value += step) {
    ticks.push(value);
  }
  return ticks.reverse();
}

function isHourlyAnalyticsSeries(series: AdminAnalyticsOverviewResponse["series"]) {
  if (series.length < 2) return true;
  const first = Date.parse(series[0].bucketStart);
  const second = Date.parse(series[1].bucketStart);
  return Number.isFinite(first) && Number.isFinite(second) && Math.abs(second - first) <= 2 * 60 * 60 * 1000;
}

function formatDateParts(value: string) {
  const parts = new Intl.DateTimeFormat("el-GR", {
    timeZone: "Europe/Athens",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(value));
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    day: byType.day ?? "--",
    month: byType.month ?? "--",
    year: byType.year ?? "----",
    hour: byType.hour ?? "00",
    minute: byType.minute ?? "00",
  };
}

function formatChartAxisLabel(value: string, isHourly: boolean) {
  const parts = formatDateParts(value);
  return isHourly ? `${parts.hour}:${parts.minute} ${greekMeridiemSuffix(parts.hour)}` : `${parts.day}/${parts.month}`;
}

function formatChartTooltipDate(value: string, isHourly: boolean) {
  const parts = formatDateParts(value);
  return isHourly ? `${parts.day}/${parts.month}/${parts.year}, ${parts.hour}:${parts.minute} ${greekMeridiemSuffix(parts.hour)}` : `${parts.day}/${parts.month}/${parts.year}`;
}

function greekMeridiemSuffix(hour: string) {
  const parsed = Number.parseInt(hour, 10);
  return Number.isFinite(parsed) && parsed < 12 ? "π.μ." : "μ.μ.";
}

function buildXAxisLabels(series: AdminAnalyticsOverviewResponse["series"], isHourly: boolean) {
  if (series.length === 0) return [];
  const step = isHourly ? (series.length <= 12 ? 1 : 2) : series.length <= 10 ? 1 : Math.ceil(series.length / 6);
  return series
    .map((point, index) => ({ point, index, label: formatChartAxisLabel(point.bucketStart, isHourly) }))
    .filter(({ index }) => index === 0 || index === series.length - 1 || index % step === 0);
}

function normalizeDeviceType(value: string | null | undefined): DeviceType {
  if (value === "Desktop" || value === "Tablet" || value === "Mobile") return value;
  return "Unknown";
}

function shortDisplayId(value: string | null | undefined) {
  if (!value) return "-";
  const compact = value.replace(/[^a-z0-9]/gi, "");
  return compact.slice(-8).toUpperCase() || value.slice(-8);
}

function attributionValue(value: string | null | undefined) {
  return value?.trim() || "-";
}

function DeviceDisplay({ type, summary }: { type: DeviceType; summary?: string | null }) {
  return (
    <span className="block whitespace-nowrap">
      <span className={["block font-semibold", type === "Unknown" ? "text-slate-400" : "text-slate-100"].join(" ")}>{type}</span>
      {summary ? <span className="block text-xs text-slate-500">{summary}</span> : null}
    </span>
  );
}

function LiveUsersCard({ data, error }: { data: AdminLiveAnalyticsResponse | null; error: string | null }) {
  return (
    <Card>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">Live users now</p>
          <h3 className="mt-2 text-3xl font-bold text-white">{data ? data.activeNow : "—"}</h3>
          <p className="mt-1 text-sm text-slate-400">Active in the last {data?.windowMinutes ?? 5} minutes.</p>
        </div>
        {error ? <span className="rounded-full bg-rose-400/10 px-2.5 py-1 text-xs font-semibold text-rose-200">Unavailable</span> : null}
      </div>
      {error ? <p className="mt-4 text-sm text-slate-400">{error}</p> : null}
      {data && data.sessions.length > 0 ? (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[680px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                <th className="px-3 py-2">Session</th>
                <th className="px-3 py-2">Current path</th>
                <th className="px-3 py-2">Last seen</th>
                <th className="px-3 py-2">Device</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {data.sessions.slice(0, 8).map((session) => (
                <tr key={session.sessionId} className="text-slate-200">
                  <td className="whitespace-nowrap px-3 py-2 font-semibold text-cyan-100">{session.shortId}</td>
                  <td className="px-3 py-2">{session.currentPath ?? "—"}</td>
                  <td className="whitespace-nowrap px-3 py-2">{formatDate(session.lastSeenAt)}</td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <DeviceDisplay type={session.deviceType} summary={session.userAgentSummary} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : data ? (
        <p className="mt-4 text-sm text-slate-400">No active public sessions right now.</p>
      ) : (
        <p className="mt-4 text-sm text-slate-400">Loading live sessions...</p>
      )}
    </Card>
  );
}

function TrafficSessionDetailsPanel() {
  const [range, setRange] = useState<AdminAnalyticsRangeKey>("today");
  const [data, setData] = useState<AdminAnalyticsSessionDetailsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const rangeOptions: Array<{ value: AdminAnalyticsRangeKey; label: string }> = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last7d", label: "Last 7 days" },
    { value: "month", label: "This month" },
  ];

  useEffect(() => {
    const token = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!token) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAdminAnalyticsSessionDetails(token, range)
      .then((nextData) => {
        if (cancelled) return;
        setData(nextData);
        setExpandedId(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load visitor session details.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [range]);

  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b border-white/10 px-5 py-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">Traffic session details</p>
            <h3 className="mt-2 text-2xl font-bold text-white">Visitor session details</h3>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-400">
              Recent public sessions grouped from analytics events with privacy-safe attribution when available.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 rounded-2xl border border-white/10 bg-slate-950/60 p-1">
            {rangeOptions.map((option) => {
              const isActive = option.value === range;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setRange(option.value)}
                  className={[
                    "rounded-xl px-3 py-2 text-sm font-semibold transition",
                    isActive ? "bg-cyan-300/15 text-cyan-100" : "text-slate-300 hover:bg-white/5 hover:text-white",
                  ].join(" ")}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {error ? <p className="px-5 py-4 text-sm text-rose-200">{error}</p> : null}
      {loading && !data ? <p className="px-5 py-6 text-sm text-slate-400">Loading visitor session details...</p> : null}
      {data && data.groups.length === 0 && !error ? <p className="px-5 py-6 text-sm text-slate-400">No visitor session details for this range yet.</p> : null}
      {data && data.groups.length > 0 ? (
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[1320px]">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.12em] text-slate-400">
                <th className="px-4 py-3 font-semibold">Session</th>
                <th className="px-4 py-3 font-semibold">First / last seen</th>
                <th className="px-4 py-3 font-semibold">Country</th>
                <th className="px-4 py-3 font-semibold">Device</th>
                <th className="px-4 py-3 font-semibold">Source</th>
                <th className="px-4 py-3 font-semibold">Page views</th>
                <th className="px-4 py-3 font-semibold">Test starts</th>
                <th className="px-4 py-3 font-semibold">Completed</th>
                <th className="px-4 py-3 font-semibold">Checkout</th>
                <th className="px-4 py-3 font-semibold">Paid</th>
                <th className="px-4 py-3 font-semibold">Pending</th>
                <th className="px-4 py-3 font-semibold">Report opens</th>
                <th className="px-4 py-3 font-semibold">Latest path</th>
                <th className="px-4 py-3 font-semibold">Details</th>
              </tr>
            </thead>
            <tbody className={["divide-y divide-white/10", loading ? "opacity-60" : ""].join(" ")}>
              {data.groups.map((group) => {
                const isExpanded = expandedId === group.id;
                return (
                  <Fragment key={group.id}>
                    <tr className="text-sm text-slate-200">
                      <td className="whitespace-nowrap px-4 py-3 font-semibold text-cyan-100">
                        <span className="block">{group.visitorLabel}</span>
                        <span className="block text-xs text-slate-500">{shortDisplayId(group.sessionId)}</span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className="block">{formatDate(group.firstSeenAt)}</span>
                        <span className="block text-xs text-slate-500">{formatDate(group.lastSeenAt)}</span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">{group.country || "Unknown"}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <DeviceDisplay type={normalizeDeviceType(group.device)} summary={group.deviceSummary} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className="block">{group.source || "Unknown"}</span>
                        {group.referrerHost ? (
                          <span className="block max-w-44 truncate text-xs text-slate-500" title={group.referrerHost}>ref {group.referrerHost}</span>
                        ) : group.utmSource ? (
                          <span className="block max-w-44 truncate text-xs text-slate-500" title={group.utmSource}>utm {group.utmSource}</span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">{group.pageViews}</td>
                      <td className="whitespace-nowrap px-4 py-3">{group.testStarts}</td>
                      <td className="whitespace-nowrap px-4 py-3">{group.completedTests}</td>
                      <td className="whitespace-nowrap px-4 py-3">{group.checkoutStarts}</td>
                      <td className="whitespace-nowrap px-4 py-3">{group.paidReports}</td>
                      <td className="whitespace-nowrap px-4 py-3">{group.pendingPayments}</td>
                      <td className="whitespace-nowrap px-4 py-3">{group.reportOpens}</td>
                      <td className="max-w-72 truncate px-4 py-3" title={group.latestPath ?? undefined}>{group.latestPath ?? "-"}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <button
                          type="button"
                          className="rounded-lg border border-white/10 px-2 py-1 text-xs font-semibold text-cyan-100 transition hover:bg-white/8"
                          onClick={() => setExpandedId(isExpanded ? null : group.id)}
                        >
                          {isExpanded ? "Hide" : "Show"} ({group.details.length})
                        </button>
                      </td>
                    </tr>
                    {isExpanded ? (
                      <tr className="bg-white/[0.025] text-sm text-slate-300">
                        <td className="px-4 py-4" colSpan={14}>
                          <div className="mb-3 flex flex-wrap gap-3 text-xs text-slate-400">
                            <span>Range: {formatDate(data.range.from)} to {formatDate(data.range.to)}</span>
                            <span>Generated: {formatDate(data.generatedAt)}</span>
                            <span>Session: {shortDisplayId(group.sessionId)}</span>
                          </div>
                          <div className="mb-3 flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-400">
                            <span>Country: <span className="text-slate-200">{group.country || "Unknown"}</span></span>
                            <span>Source: <span className="text-slate-200">{group.source || "Unknown"}</span></span>
                            <span>Referrer: <span className="text-slate-200">{attributionValue(group.referrerHost)}</span></span>
                            <span>UTM source: <span className="text-slate-200">{attributionValue(group.utmSource)}</span></span>
                            <span>UTM medium: <span className="text-slate-200">{attributionValue(group.utmMedium)}</span></span>
                            <span>UTM campaign: <span className="text-slate-200">{attributionValue(group.utmCampaign)}</span></span>
                            <span>Landing: <span className="text-slate-200">{attributionValue(group.landingPath)}</span></span>
                          </div>
                          {group.details.length > 0 ? (
                            <div className="overflow-x-auto rounded-xl border border-white/10">
                              <table className="w-full min-w-[780px]">
                                <thead>
                                  <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.12em] text-slate-500">
                                    <th className="px-3 py-2 font-semibold">Time</th>
                                    <th className="px-3 py-2 font-semibold">Event</th>
                                    <th className="px-3 py-2 font-semibold">Path</th>
                                    <th className="px-3 py-2 font-semibold">Attempt</th>
                                    <th className="px-3 py-2 font-semibold">Order</th>
                                    <th className="px-3 py-2 font-semibold">Report</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-white/10">
                                  {group.details.map((detail) => (
                                    <tr key={`${group.id}-${detail.time}-${detail.type}-${detail.path ?? ""}`}>
                                      <td className="whitespace-nowrap px-3 py-2">{formatDate(detail.time)}</td>
                                      <td className="whitespace-nowrap px-3 py-2">{detail.label}</td>
                                      <td className="max-w-72 truncate px-3 py-2" title={detail.path ?? undefined}>{detail.path ?? "-"}</td>
                                      <td className="whitespace-nowrap px-3 py-2">{shortDisplayId(detail.attemptId)}</td>
                                      <td className="whitespace-nowrap px-3 py-2">{shortDisplayId(detail.orderId)}</td>
                                      <td className="whitespace-nowrap px-3 py-2">{shortDisplayId(detail.reportId)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-400">No detailed event timeline is available for this session group.</p>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </Card>
  );
}

function TestsSection({ metrics }: { metrics: AdminMetricsResponse }) {
  const [filters, setFilters] = useState<AdminAttemptsFilters>({
    status: "all",
    payment: "all",
    range: "lifetime",
    from: "",
    to: "",
    search: "",
    page: 1,
    pageSize: 20,
  });
  const [searchInput, setSearchInput] = useState("");
  const [fromInput, setFromInput] = useState("");
  const [toInput, setToInput] = useState("");
  const [attemptsData, setAttemptsData] = useState<AdminAttemptsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [attemptsError, setAttemptsError] = useState<string | null>(null);
  const [reportPreview, setReportPreview] = useState<AdminReportPreviewState | null>(null);
  const [reportPreviewLoadingId, setReportPreviewLoadingId] = useState<string | null>(null);
  const [reportPreviewError, setReportPreviewError] = useState<string | null>(null);
  const [labelSavingId, setLabelSavingId] = useState<string | null>(null);
  const [labelError, setLabelError] = useState<string | null>(null);

  useEffect(() => {
    const token = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!token) return;

    let cancelled = false;
    setLoading(true);
    setAttemptsError(null);

    fetchAdminAttempts(token, filters)
      .then((data) => {
        if (cancelled) return;
        setAttemptsData(data);
      })
      .catch(() => {
        if (cancelled) return;
        setAttemptsError("Could not load attempts.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const nextSearch = searchInput.trim();
      setFilters((current) => {
        if (current.search === nextSearch) {
          return current;
        }

        return { ...current, search: nextSearch, page: 1 };
      });
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchInput]);

  function updateFilter(next: Partial<AdminAttemptsFilters>) {
    setFilters((current) => ({ ...current, ...next, page: 1 }));
  }

  function handleApplyFilters(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFilters((current) => ({
      ...current,
      search: searchInput.trim(),
      from: fromInput,
      to: toInput,
      page: 1,
    }));
  }

  function handleSearchInputChange(value: string) {
    setSearchInput(value);
  }

  async function handleViewReport(attempt: AdminAttemptListItem) {
    const token = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!token) return;

    setReportPreviewError(null);
    setReportPreviewLoadingId(attempt.attemptId);
    setReportPreview(null);

    try {
      const preview = await fetchAdminReportPreview(token, attempt.attemptId);
      setReportPreview({ ...preview, attemptId: attempt.attemptId, attemptShortId: attempt.shortId });
    } catch {
      setReportPreviewError("Could not load report preview.");
    } finally {
      setReportPreviewLoadingId(null);
    }
  }

  async function handleSaveLabel(attemptId: string, label: string) {
    const token = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!token) return;

    const trimmedLabel = label.trim();
    if (trimmedLabel.length > 80) {
      setLabelError("Label must be 80 characters or fewer.");
      return;
    }

    setLabelError(null);
    setLabelSavingId(attemptId);

    try {
      const result = await updateAdminAttemptLabel(token, attemptId, trimmedLabel || null);
      setAttemptsData((current) =>
        current
          ? {
              ...current,
              items: current.items.map((attempt) =>
                attempt.attemptId === attemptId ? { ...attempt, adminLabel: result.adminLabel } : attempt
              ),
            }
          : current
      );
    } catch {
      setLabelError("Could not update label.");
      throw new Error("label_update_failed");
    } finally {
      setLabelSavingId(null);
    }
  }

  const totalPages = attemptsData?.totalPages ?? 1;
  const totalItems = attemptsData?.totalItems ?? 0;

  return (
    <div className="space-y-5">
      <SectionHeader
        eyebrow="Tests"
        title="Tests / Attempts"
        description="Manage and review high-level test attempts with protected filters, search, and pagination."
      />

      <div className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <AdminMetricCard label="Total attempts" value={String(metrics.overview.totalAttempts)} />
        <AdminMetricCard label="Completed tests" value={String(metrics.overview.completedTests)} />
        <AdminMetricCard label="Completion rate" value={formatPercent(metrics.overview.completionRatePercent)} />
      </div>

      <Card>
        <form className="grid gap-4 xl:grid-cols-[repeat(6,minmax(0,1fr))_auto]" onSubmit={handleApplyFilters}>
          <AdminSelect
            label="Status"
            value={filters.status}
            onChange={(value) => updateFilter({ status: value as AttemptStatusFilter })}
            options={[
              { value: "all", label: "All" },
              { value: "completed", label: "Completed" },
              { value: "incomplete", label: "Incomplete" },
            ]}
          />
          <AdminSelect
            label="Payment"
            value={filters.payment}
            onChange={(value) => updateFilter({ payment: value as AttemptPaymentFilter })}
            options={[
              { value: "all", label: "All" },
              { value: "completed_paid", label: "Completed + paid report" },
              { value: "completed_unpaid", label: "Completed + unpaid report" },
            ]}
          />
          <AdminSelect
            label="Date range"
            value={filters.range}
            onChange={(value) => updateFilter({ range: value as AttemptRangeFilter })}
            options={[
              { value: "lifetime", label: "Lifetime" },
              { value: "today", label: "Today" },
              { value: "last7d", label: "Last 7 days" },
              { value: "month", label: "This month" },
              { value: "custom", label: "Custom" },
            ]}
          />
          <label className="grid gap-2 text-sm font-semibold text-slate-200">
            From
            <Input
              type="date"
              value={fromInput}
              disabled={filters.range !== "custom"}
              onChange={(event) => setFromInput(event.target.value)}
            />
          </label>
          <label className="grid gap-2 text-sm font-semibold text-slate-200">
            To
            <Input
              type="date"
              value={toInput}
              disabled={filters.range !== "custom"}
              onChange={(event) => setToInput(event.target.value)}
            />
          </label>
          <label className="grid gap-2 text-sm font-semibold text-slate-200">
            Search
            <Input
              type="search"
              value={searchInput}
              placeholder="Attempt ID or email"
              onChange={(event) => handleSearchInputChange(event.target.value)}
            />
          </label>
          <div className="flex items-end gap-3">
            <AdminSelect
              label="Rows"
              value={String(filters.pageSize)}
              onChange={(value) => updateFilter({ pageSize: Number(value) })}
              options={[
                { value: "20", label: "20" },
                { value: "50", label: "50" },
                { value: "100", label: "100" },
              ]}
            />
            <Button type="submit" className="min-h-11 px-5">
              Apply
            </Button>
          </div>
        </form>
      </Card>

      {attemptsError ? <InfoCard>{attemptsError}</InfoCard> : null}
      {loading && !attemptsData ? <InfoCard>Loading attempts...</InfoCard> : null}
      {attemptsData ? (
        <AttemptsTable
          attempts={attemptsData.items}
          loading={loading}
          reportPreviewLoadingId={reportPreviewLoadingId}
          labelSavingId={labelSavingId}
          onSaveLabel={handleSaveLabel}
          onViewReport={handleViewReport}
        />
      ) : null}
      {labelError ? <InfoCard>{labelError}</InfoCard> : null}
      {reportPreviewError ? <InfoCard>{reportPreviewError}</InfoCard> : null}
      {reportPreview ? <ReportPreviewModal preview={reportPreview} onClose={() => setReportPreview(null)} /> : null}

      <Card className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-300">
          Page {attemptsData?.page ?? filters.page} of {totalPages} · {totalItems} total attempts
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={loading || filters.page <= 1}
            onClick={() => setFilters((current) => ({ ...current, page: Math.max(1, current.page - 1) }))}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={loading || filters.page >= totalPages}
            onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
          >
            Next
          </Button>
        </div>
      </Card>

      <div className="grid gap-5 md:grid-cols-2">
        <BreakdownList title="Test status" items={metrics.breakdowns.statusCounts} labelKey="status" />
        <BreakdownList title="Age bands" items={metrics.breakdowns.ageBandCounts} labelKey="ageBand" emptyLabel="Unknown" />
      </div>
    </div>
  );
}

function OrdersSection({ metrics }: { metrics: AdminMetricsResponse }) {
  const [filters, setFilters] = useState<AdminOrdersFilters>({
    status: "all",
    report: "all",
    email: "all",
    range: "lifetime",
    from: "",
    to: "",
    search: "",
    page: 1,
    pageSize: 20,
  });
  const [searchInput, setSearchInput] = useState("");
  const [fromInput, setFromInput] = useState("");
  const [toInput, setToInput] = useState("");
  const [ordersData, setOrdersData] = useState<AdminOrdersResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);

  useEffect(() => {
    const token = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!token) return;

    let cancelled = false;
    setLoading(true);
    setOrdersError(null);

    fetchAdminOrders(token, filters)
      .then((data) => {
        if (cancelled) return;
        setOrdersData(data);
      })
      .catch(() => {
        if (cancelled) return;
        setOrdersError("Could not load orders.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters]);

  function updateFilter(next: Partial<AdminOrdersFilters>) {
    setFilters((current) => ({ ...current, ...next, page: 1 }));
  }

  function handleApplyFilters(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFilters((current) => ({
      ...current,
      search: searchInput.trim(),
      from: fromInput,
      to: toInput,
      page: 1,
    }));
  }

  const totalPages = ordersData?.totalPages ?? 1;
  const totalItems = ordersData?.totalItems ?? 0;

  return (
    <div className="space-y-5">
      <SectionHeader
        eyebrow="Orders"
        title="Orders"
        description="Review paid, pending, and report-linked orders with protected filters and pagination."
      />
      <div className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <AdminMetricCard label="Paid reports" value={String(metrics.sales.paidReports)} />
        <AdminMetricCard label="Gross revenue" value={metrics.sales.grossRevenueFormatted} />
        <AdminMetricCard label="Pending orders" value={String(metrics.sales.pendingOrders)} />
        <AdminMetricCard label="Paid conversion" value={formatPercent(metrics.sales.paidConversionRatePercent)} />
        <AdminMetricCard label="Refunds" value={metrics.sales.refunds} />
      </div>

      <Card>
        <form className="grid gap-4 xl:grid-cols-[repeat(7,minmax(0,1fr))_auto]" onSubmit={handleApplyFilters}>
          <AdminSelect
            label="Status"
            value={filters.status}
            onChange={(value) => updateFilter({ status: value as OrderStatusFilter })}
            options={[
              { value: "all", label: "All" },
              { value: "paid", label: "Paid" },
              { value: "pending", label: "Pending" },
              { value: "failed", label: "Failed" },
              { value: "expired", label: "Expired" },
            ]}
          />
          <AdminSelect
            label="Report"
            value={filters.report}
            onChange={(value) => updateFilter({ report: value as OrderReportFilter })}
            options={[
              { value: "all", label: "All" },
              { value: "with_report", label: "With report" },
              { value: "without_report", label: "Without report" },
            ]}
          />
          <AdminSelect
            label="Email"
            value={filters.email}
            onChange={(value) => updateFilter({ email: value as OrderEmailFilter })}
            options={[
              { value: "all", label: "All" },
              { value: "email_sent", label: "Email sent" },
              { value: "email_failed", label: "Email failed" },
              { value: "email_missing", label: "Email missing" },
            ]}
          />
          <AdminSelect
            label="Date range"
            value={filters.range}
            onChange={(value) => updateFilter({ range: value as OrderRangeFilter })}
            options={[
              { value: "lifetime", label: "Lifetime" },
              { value: "today", label: "Today" },
              { value: "last7d", label: "Last 7 days" },
              { value: "month", label: "This month" },
              { value: "custom", label: "Custom" },
            ]}
          />
          <label className="grid gap-2 text-sm font-semibold text-slate-200">
            From
            <Input
              type="date"
              value={fromInput}
              disabled={filters.range !== "custom"}
              onChange={(event) => setFromInput(event.target.value)}
            />
          </label>
          <label className="grid gap-2 text-sm font-semibold text-slate-200">
            To
            <Input
              type="date"
              value={toInput}
              disabled={filters.range !== "custom"}
              onChange={(event) => setToInput(event.target.value)}
            />
          </label>
          <label className="grid gap-2 text-sm font-semibold text-slate-200">
            Search
            <Input
              type="search"
              value={searchInput}
              placeholder="Order, attempt, email, report, session"
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>
          <div className="flex items-end gap-3">
            <AdminSelect
              label="Rows"
              value={String(filters.pageSize)}
              onChange={(value) => updateFilter({ pageSize: Number(value) })}
              options={[
                { value: "20", label: "20" },
                { value: "50", label: "50" },
                { value: "100", label: "100" },
              ]}
            />
            <Button type="submit" className="min-h-11 px-5">
              Apply
            </Button>
          </div>
        </form>
      </Card>

      {ordersError ? <InfoCard>{ordersError}</InfoCard> : null}
      {loading && !ordersData ? <InfoCard>Loading orders...</InfoCard> : null}
      {ordersData ? <OrdersTable orders={ordersData.items} loading={loading} /> : null}

      <Card className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-300">
          Page {ordersData?.page ?? filters.page} of {totalPages} · {totalItems} total order groups
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={loading || filters.page <= 1}
            onClick={() => setFilters((current) => ({ ...current, page: Math.max(1, current.page - 1) }))}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={loading || filters.page >= totalPages}
            onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
          >
            Next
          </Button>
        </div>
      </Card>

      <div className="grid gap-5 md:grid-cols-2">
        <BreakdownList title="Order status" items={metrics.sales.statusCounts} labelKey="status" />
        <BreakdownList title="Report status" items={metrics.sales.reportStatusCounts} labelKey="status" />
      </div>
    </div>
  );
}

function EmailsSection({ metrics }: { metrics: AdminMetricsResponse }) {
  const [filters, setFilters] = useState<AdminEmailsFilters>({
    type: "all",
    status: "all",
    paid: "all",
    range: "lifetime",
    from: "",
    to: "",
    search: "",
    page: 1,
    pageSize: 20,
  });
  const [searchInput, setSearchInput] = useState("");
  const [fromInput, setFromInput] = useState("");
  const [toInput, setToInput] = useState("");
  const [emailsData, setEmailsData] = useState<AdminEmailsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailsError, setEmailsError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [contactResend, setContactResend] = useState<AdminEmailListItem | null>(null);
  const [resendReportId, setResendReportId] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [manualEmail, setManualEmail] = useState("");
  const [manualReportId, setManualReportId] = useState("");
  const [manualLoading, setManualLoading] = useState(false);
  const [manualStatus, setManualStatus] = useState<string | null>(null);
  const [manualError, setManualError] = useState<string | null>(null);

  useEffect(() => {
    const token = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!token) return;

    let cancelled = false;
    setLoading(true);
    setEmailsError(null);

    fetchAdminEmails(token, filters)
      .then((data) => {
        if (cancelled) return;
        setEmailsData(data);
      })
      .catch(() => {
        if (cancelled) return;
        setEmailsError("Could not load email records.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters, refreshKey]);

  function updateFilter(next: Partial<AdminEmailsFilters>) {
    setFilters((current) => ({ ...current, ...next, page: 1 }));
  }

  function handleApplyFilters(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFilters((current) => ({
      ...current,
      search: searchInput.trim(),
      from: fromInput,
      to: toInput,
      page: 1,
    }));
  }

  function openContactResend(email: AdminEmailListItem) {
    setContactResend(email);
    setResendReportId("");
    setResendStatus(null);
    setResendError(null);
  }

  async function handleContactResend(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const token = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!token || !contactResend) return;

    setResendLoading(true);
    setResendStatus(null);
    setResendError(null);
    try {
      const result = await sendAdminReportDeliveryEmail(token, {
        email: contactResend.email,
        reportOrAttemptId: resendReportId.trim(),
        source: "admin_contact_resend",
        contactId: contactResend.id,
      });
      setResendStatus(`Email ${result.status}.`);
      setRefreshKey((current) => current + 1);
    } catch (error) {
      setResendError(error instanceof Error ? error.message : "Could not send report email.");
    } finally {
      setResendLoading(false);
    }
  }

  async function handleManualSend(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const token = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!token) return;

    setManualLoading(true);
    setManualStatus(null);
    setManualError(null);
    try {
      const result = await sendAdminReportDeliveryEmail(token, {
        email: manualEmail.trim(),
        reportOrAttemptId: manualReportId.trim(),
        source: "admin_manual_report_delivery",
      });
      setManualStatus(`Email ${result.status}.`);
      setManualEmail("");
      setManualReportId("");
      setRefreshKey((current) => current + 1);
    } catch (error) {
      setManualError(error instanceof Error ? error.message : "Could not send report email.");
    } finally {
      setManualLoading(false);
    }
  }

  const totalPages = emailsData?.totalPages ?? 1;
  const totalItems = emailsData?.totalItems ?? 0;
  const deliverySent = emailsData?.items.filter((item) => item.type === "report_delivery" && item.status === "sent").length;
  const deliveryFailed = emailsData?.items.filter((item) => item.type === "report_delivery" && item.status === "failed").length;

  return (
    <div className="space-y-5">
      <SectionHeader
        eyebrow="Emails"
        title="Emails"
        description="Review contact messages, progress reminders, and report delivery emails."
      />
      <div className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <AdminMetricCard label="Contact messages" value={String(metrics.contact.contactMessageCount)} />
        <AdminMetricCard label="Reminder signups" value={String(metrics.contact.reminderSignupCount)} />
        <AdminMetricCard label="Report delivery sent" value={deliverySent === undefined ? "—" : String(deliverySent)} />
        <AdminMetricCard label="Report delivery failed" value={deliveryFailed === undefined ? "—" : String(deliveryFailed)} />
      </div>

      <Card>
        <form className="grid gap-4 xl:grid-cols-[repeat(7,minmax(0,1fr))_auto]" onSubmit={handleApplyFilters}>
          <AdminSelect
            label="Type"
            value={filters.type}
            onChange={(value) => updateFilter({ type: value as EmailTypeFilter })}
            options={[
              { value: "all", label: "All" },
              { value: "contact", label: "Contact" },
              { value: "progress_reminder", label: "Progress reminder" },
              { value: "report_delivery", label: "Report delivery" },
            ]}
          />
          <AdminSelect
            label="Status"
            value={filters.status}
            onChange={(value) => updateFilter({ status: value as EmailStatusFilter })}
            options={[
              { value: "all", label: "All" },
              { value: "new", label: "New" },
              { value: "sent", label: "Sent" },
              { value: "failed", label: "Failed" },
              { value: "skipped", label: "Skipped" },
            ]}
          />
          <AdminSelect
            label="Paid"
            value={filters.paid}
            onChange={(value) => updateFilter({ paid: value as EmailPaidFilter })}
            options={[
              { value: "all", label: "All" },
              { value: "paid_customer", label: "Paid customers" },
              { value: "non_paid_customer", label: "Non-paid" },
              { value: "unknown", label: "Unknown" },
            ]}
          />
          <AdminSelect
            label="Date range"
            value={filters.range}
            onChange={(value) => updateFilter({ range: value as EmailRangeFilter })}
            options={[
              { value: "lifetime", label: "Lifetime" },
              { value: "today", label: "Today" },
              { value: "last7d", label: "Last 7 days" },
              { value: "month", label: "This month" },
              { value: "custom", label: "Custom" },
            ]}
          />
          <label className="grid gap-2 text-sm font-semibold text-slate-200">
            From
            <Input
              type="date"
              value={fromInput}
              disabled={filters.range !== "custom"}
              onChange={(event) => setFromInput(event.target.value)}
            />
          </label>
          <label className="grid gap-2 text-sm font-semibold text-slate-200">
            To
            <Input
              type="date"
              value={toInput}
              disabled={filters.range !== "custom"}
              onChange={(event) => setToInput(event.target.value)}
            />
          </label>
          <label className="grid gap-2 text-sm font-semibold text-slate-200">
            Search
            <Input
              type="search"
              value={searchInput}
              placeholder="Email, subject, message, provider ID"
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>
          <div className="flex items-end gap-3">
            <AdminSelect
              label="Rows"
              value={String(filters.pageSize)}
              onChange={(value) => updateFilter({ pageSize: Number(value) })}
              options={[
                { value: "20", label: "20" },
                { value: "50", label: "50" },
                { value: "100", label: "100" },
              ]}
            />
            <Button type="submit" className="min-h-11 px-5">
              Apply
            </Button>
          </div>
        </form>
      </Card>

      {emailsError ? <InfoCard>{emailsError}</InfoCard> : null}
      {loading && !emailsData ? <InfoCard>Loading email records...</InfoCard> : null}
      {emailsData ? <EmailsTable emails={emailsData.items} loading={loading} onSendReportEmail={openContactResend} /> : null}

      <Card>
        <h3 className="text-lg font-semibold text-white">Send Premium Report email</h3>
        <p className="mt-2 text-sm leading-6 text-slate-400">
          Send an existing paid/unlocked Premium Report email from the server. The secure link is not shown in admin.
        </p>
        <form className="mt-5 grid gap-4 lg:grid-cols-[1fr_1fr_auto]" onSubmit={handleManualSend}>
          <label className="grid gap-2 text-sm font-semibold text-slate-200">
            Recipient email
            <Input
              type="email"
              value={manualEmail}
              placeholder="customer@example.com"
              onChange={(event) => setManualEmail(event.target.value)}
            />
          </label>
          <label className="grid gap-2 text-sm font-semibold text-slate-200">
            Report ID or Attempt ID
            <Input
              value={manualReportId}
              placeholder="TBT-RPT-... or attempt_..."
              onChange={(event) => setManualReportId(event.target.value)}
            />
          </label>
          <div className="flex items-end">
            <Button type="submit" disabled={manualLoading} className="min-h-11 whitespace-nowrap">
              {manualLoading ? "Sending..." : "Send Premium Report email"}
            </Button>
          </div>
        </form>
        <p className="mt-3 text-xs leading-5 text-slate-500">Βεβαιώσου ότι το report ανήκει στον παραλήπτη.</p>
        {manualStatus ? <p className="mt-3 text-sm font-semibold text-emerald-200">{manualStatus}</p> : null}
        {manualError ? <p className="mt-3 text-sm font-semibold text-rose-200">{manualError}</p> : null}
      </Card>

      {contactResend ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 px-4 py-8">
          <Card className="w-full max-w-lg">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">Contact resend</p>
                <h3 className="mt-2 text-2xl font-bold text-white">Send Premium Report email</h3>
              </div>
              <button type="button" className="text-sm font-semibold text-slate-400 hover:text-white" onClick={() => setContactResend(null)}>
                Close
              </button>
            </div>
            <form className="mt-5 space-y-4" onSubmit={handleContactResend}>
              <label className="grid gap-2 text-sm font-semibold text-slate-200">
                Recipient email
                <Input type="email" value={contactResend.email} disabled />
              </label>
              <label className="grid gap-2 text-sm font-semibold text-slate-200">
                Report ID or Attempt ID
                <Input
                  value={resendReportId}
                  placeholder="TBT-RPT-... or attempt_..."
                  onChange={(event) => setResendReportId(event.target.value)}
                />
              </label>
              <p className="rounded-lg border border-amber-300/20 bg-amber-300/10 p-3 text-sm leading-6 text-amber-100">
                Βεβαιώσου ότι το report ανήκει στον παραλήπτη.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button type="submit" disabled={resendLoading} className="min-h-11">
                  {resendLoading ? "Sending..." : "Send Premium Report email"}
                </Button>
                <Button type="button" variant="secondary" onClick={() => setContactResend(null)}>
                  Cancel
                </Button>
              </div>
              {resendStatus ? <p className="text-sm font-semibold text-emerald-200">{resendStatus}</p> : null}
              {resendError ? <p className="text-sm font-semibold text-rose-200">{resendError}</p> : null}
            </form>
          </Card>
        </div>
      ) : null}

      <Card className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-300">
          Page {emailsData?.page ?? filters.page} of {totalPages} · {totalItems} total email records
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={loading || filters.page <= 1}
            onClick={() => setFilters((current) => ({ ...current, page: Math.max(1, current.page - 1) }))}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={loading || filters.page >= totalPages}
            onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}
          >
            Next
          </Button>
        </div>
      </Card>
    </div>
  );
}

function UsageSection() {
  const [usage, setUsage] = useState<AdminUsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);

  useEffect(() => {
    const token = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!token) return;

    let cancelled = false;
    setLoading(true);
    setUsageError(null);

    fetchAdminUsage(token)
      .then((data) => {
        if (cancelled) return;
        setUsage(data);
      })
      .catch(() => {
        if (cancelled) return;
        setUsageError("Could not load usage data.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const countRows = usage
    ? [
        { label: "Sessions", value: usage.counts.sessions },
        { label: "Analytics sessions", value: usage.counts.analyticsSessions },
        { label: "Analytics events", value: usage.counts.analyticsEvents },
        { label: "Attempts", value: usage.counts.attempts },
        { label: "Attempt items", value: usage.counts.attemptItems },
        { label: "Orders", value: usage.counts.orders },
        { label: "Reports", value: usage.counts.reports },
        { label: "Contact messages", value: usage.counts.contactMessages },
        { label: "Email logs", value: usage.counts.emailLogs },
        { label: "Stripe events", value: usage.counts.stripeEvents },
        { label: "Question items", value: usage.counts.questionItems },
        { label: "Question stats", value: usage.counts.questionStats },
        { label: "Admin events", value: usage.counts.adminEvents },
      ]
    : [];
  const configRows = usage
    ? [
        { label: "Stripe configured", value: usage.config.stripeConfigured },
        { label: "Resend configured", value: usage.config.resendConfigured },
        { label: "Report token configured", value: usage.config.reportTokenConfigured },
        { label: "Admin token configured", value: usage.config.adminTokenConfigured },
        { label: "Public site URL configured", value: usage.config.publicSiteUrlConfigured },
        { label: "Cloudflare usage configured", value: Boolean(usage.config.cloudflareConfigured) },
      ]
    : [];
  const officialStorageBytes = usage?.cloudflare.d1.storageBytes ?? null;
  const officialStorageLimitBytes = usage?.cloudflare.d1.storageLimitBytes ?? null;
  const estimatedTotalBytes = usage?.databaseEstimate.estimatedTotalBytes ?? 0;
  const hasCloudflareStorage = officialStorageBytes !== null && estimatedTotalBytes > 0;
  const tableContributionRows = usage
    ? usage.databaseEstimate.byTable.map((row) => {
        const estimatedShare = estimatedTotalBytes > 0 ? row.estimatedBytes / estimatedTotalBytes : 0;
        const normalizedOfficialBytes = hasCloudflareStorage ? estimatedShare * officialStorageBytes : row.estimatedBytes;
        const percentOfUsed = hasCloudflareStorage && officialStorageBytes > 0 ? (normalizedOfficialBytes / officialStorageBytes) * 100 : row.estimatedPercentOfEstimate;
        const percentOfLimit =
          hasCloudflareStorage && officialStorageLimitBytes && officialStorageLimitBytes > 0
            ? (normalizedOfficialBytes / officialStorageLimitBytes) * 100
            : null;

        return {
          ...row,
          payloadEstimateBytes: row.estimatedBytes,
          estimatedBytes: normalizedOfficialBytes,
          normalizedOfficialBytes,
          percentOfUsed,
          percentOfLimit,
          primaryPercent: percentOfLimit ?? percentOfUsed,
        };
      })
    : [];

  return (
    <div className="space-y-5">
      <SectionHeader
        eyebrow="Usage"
        title="Usage & System"
        description="App-level storage, operational health, and configuration status."
      />

      {usageError ? <InfoCard>{usageError}</InfoCard> : null}
      {loading && !usage ? <InfoCard>Loading usage data...</InfoCard> : null}

      {usage ? (
        <>
          <div className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
            <AdminMetricCard label="Pending orders" value={String(usage.health.pendingOrders)} />
            <AdminMetricCard label="Expired orders" value={String(usage.health.expiredOrders)} />
            <AdminMetricCard label="Failed orders" value={String(usage.health.failedOrders)} />
            <AdminMetricCard label="Latest completed attempt" value={formatDate(usage.health.latestCompletedAttemptAt)} />
            <AdminMetricCard label="Latest paid order" value={formatDate(usage.health.latestPaidOrderAt)} />
            <AdminMetricCard label="Latest email failure" value={formatDate(usage.health.latestEmailFailedAt)} />
          </div>

          <Card>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Cloudflare D1 usage</h3>
                <p className="mt-1 text-sm leading-6 text-slate-400">
                  Storage total is reported by Cloudflare. Read/write usage is shown for today.
                </p>
              </div>
              <CloudflareStatusBadge status={usage.cloudflare.status} />
            </div>
            {usage.cloudflare.status === "missing_config" ? (
              <p className="mt-4 text-sm leading-6 text-slate-400">Cloudflare usage configuration is missing, so real D1 metrics are unavailable.</p>
            ) : null}
            {usage.cloudflare.status === "error" ? (
              <p className="mt-4 text-sm leading-6 text-amber-200">{usage.cloudflare.errorMessage ?? "Some Cloudflare D1 metrics are unavailable."}</p>
            ) : null}
            <div className="mt-5 space-y-5">
              <UsageProgressBar
                label="D1 storage"
                valueLabel={
                  usage.cloudflare.d1.storageBytes === null || usage.cloudflare.d1.storageLimitBytes === null
                    ? "Unavailable"
                    : `${formatBytes(usage.cloudflare.d1.storageBytes)} / ${formatBytes(usage.cloudflare.d1.storageLimitBytes)} · ${formatBytes(
                        usage.cloudflare.d1.storageRemainingBytes ?? 0
                      )} remaining`
                }
                percent={usage.cloudflare.d1.storageUsedPercent ?? 0}
              />
              <UsageProgressBar
                label="Rows read today"
                valueLabel={
                  usage.cloudflare.d1.rowsReadToday === null || usage.cloudflare.d1.rowsReadDailyLimit === null
                    ? "Unavailable"
                    : `${usage.cloudflare.d1.rowsReadToday.toLocaleString("el-GR")} / ${usage.cloudflare.d1.rowsReadDailyLimit.toLocaleString("el-GR")}`
                }
                percent={usage.cloudflare.d1.rowsReadUsedPercent ?? 0}
              />
              <UsageProgressBar
                label="Rows written today"
                valueLabel={
                  usage.cloudflare.d1.rowsWrittenToday === null || usage.cloudflare.d1.rowsWrittenDailyLimit === null
                    ? "Unavailable"
                    : `${usage.cloudflare.d1.rowsWrittenToday.toLocaleString("el-GR")} / ${usage.cloudflare.d1.rowsWrittenDailyLimit.toLocaleString("el-GR")}`
                }
                percent={usage.cloudflare.d1.rowsWrittenUsedPercent ?? 0}
              />
            </div>
            <p className="mt-5 text-xs leading-5 text-slate-500">
              Limits use configured/default plan values. Traffic, visitors, and Pages build metrics are intentionally not included in this phase.
            </p>
          </Card>

          <Card>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Estimated table contribution</h3>
                <p className="mt-1 text-sm leading-6 text-slate-400">
                  {hasCloudflareStorage
                    ? "Official D1 storage is reported by Cloudflare. Per-table contribution is estimated from database row/text sizes; main percentages use the total D1 storage limit."
                    : "Estimated from database row counts and text/JSON field sizes. This is not actual Cloudflare D1 storage quota usage."}
                </p>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-200">{hasCloudflareStorage ? "Official D1 used" : "Estimated total"}</p>
                <p className="mt-1 text-2xl font-bold text-white">{formatBytes(hasCloudflareStorage ? officialStorageBytes : usage.databaseEstimate.estimatedTotalBytes)}</p>
                {hasCloudflareStorage ? <p className="mt-1 text-xs text-slate-500">Estimated payload: {formatBytes(usage.databaseEstimate.estimatedTotalBytes)}</p> : null}
              </div>
            </div>
            <div className="mt-5 space-y-4">
              {tableContributionRows.length === 0 ? (
                <p className="text-sm text-slate-400">No database estimate data yet.</p>
              ) : (
                tableContributionRows.map((row) => (
                  <UsageProgressBar
                    key={row.table}
                    label={row.label}
                    detailLabel={
                      hasCloudflareStorage
                        ? `${formatUsagePercent(row.primaryPercent)} of D1 limit - Share of current used storage: ${formatUsagePercent(
                            row.percentOfUsed
                          )} - Payload estimate: ${formatBytes(row.payloadEstimateBytes)}`
                        : undefined
                    }
                    valueLabel={`${formatBytes(row.estimatedBytes)} · ${row.rows.toLocaleString("el-GR")} rows`}
                    percent={row.primaryPercent}
                  />
                ))
              )}
            </div>
            <p className="mt-5 text-xs leading-5 text-slate-500">
              Per-table contribution is estimated, not an exact physical table size. The top D1 storage bar remains the official Cloudflare-reported value.
            </p>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold text-white">Email usage</h3>
            <p className="mt-1 text-sm leading-6 text-slate-400">
              Based on local email delivery logs. Limits use the current Resend free-plan defaults for this phase.
            </p>
            <div className="mt-5 space-y-5">
              <UsageProgressBar
                label="Today"
                valueLabel={`${usage.resendUsage.sentToday.toLocaleString("el-GR")} / ${usage.resendUsage.dailyLimit.toLocaleString("el-GR")} sent`}
                percent={usage.resendUsage.usedPercentToday}
              />
              <UsageProgressBar
                label="This month"
                valueLabel={`${usage.resendUsage.sentThisMonth.toLocaleString("el-GR")} / ${usage.resendUsage.monthlyLimit.toLocaleString("el-GR")} sent`}
                percent={usage.resendUsage.usedPercentMonth}
              />
            </div>
            <div className="mt-5 grid gap-3 text-sm text-slate-300 sm:grid-cols-2">
              <HealthLine label="Failed today" value={String(usage.resendUsage.failedToday)} />
              <HealthLine label="Skipped today" value={String(usage.resendUsage.skippedToday)} />
              <HealthLine label="Failed this month" value={String(usage.resendUsage.failedThisMonth)} />
              <HealthLine label="Skipped this month" value={String(usage.resendUsage.skippedThisMonth)} />
            </div>
          </Card>

          <TableCard title="Database rows" emptyMessage="No row count data yet." isEmpty={countRows.length === 0}>
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500">
                <th className="px-4 py-3">Table</th>
                <th className="px-4 py-3">Rows</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {countRows.map((row) => (
                <tr key={row.label} className="text-sm text-slate-200">
                  <td className="px-4 py-3 font-semibold text-cyan-100">{row.label}</td>
                  <td className="px-4 py-3">{row.value.toLocaleString("el-GR")}</td>
                </tr>
              ))}
            </tbody>
          </TableCard>

          <div className="grid gap-5 lg:grid-cols-2">
            <Card>
              <h3 className="text-lg font-semibold text-white">Operational health</h3>
              <div className="mt-4 space-y-3 text-sm">
                <HealthLine label="Latest report unlocked" value={formatDate(usage.health.latestReportUnlockedAt)} />
                <HealthLine label="Latest Stripe event" value={formatDate(usage.health.latestStripeEventAt)} />
                <HealthLine label="Stripe event type" value={usage.health.latestStripeEventType ?? "--"} />
                <HealthLine label="Stripe event status" value={usage.health.latestStripeEventStatus ?? "--"} />
                <HealthLine label="Latest email sent" value={formatDate(usage.health.latestEmailSentAt)} />
                <HealthLine label="Latest email failed" value={formatDate(usage.health.latestEmailFailedAt)} />
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-white">Configuration</h3>
              <div className="mt-4 space-y-3">
                {configRows.map((row) => (
                  <ConfigStatusLine key={row.label} label={row.label} configured={row.value} />
                ))}
              </div>
            </Card>
          </div>

          <InfoCard>
            <span className="block">{usage.notes[0]}</span>
            <span className="mt-2 block">{usage.notes[1]}</span>
          </InfoCard>
        </>
      ) : null}

      <InfoCard>
        No destructive actions are available here. True Cloudflare bandwidth, request, D1 storage, and read/write usage should be checked in
        Cloudflare dashboard until a dedicated API integration is added.
      </InfoCard>
    </div>
  );
}

function HealthLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-3 last:border-b-0 last:pb-0">
      <span className="text-slate-400">{label}</span>
      <span className="text-right font-semibold text-slate-100">{value}</span>
    </div>
  );
}

function UsageProgressBar({ label, valueLabel, detailLabel, percent }: { label: string; valueLabel: string; detailLabel?: string; percent: number }) {
  const widthPercent = Math.min(100, Math.max(0, percent));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-4 text-sm">
        <span className="font-semibold text-slate-200">{label}</span>
        <span className="text-right text-slate-400">
          {valueLabel} · {formatUsagePercent(percent)}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-800">
        <div className="h-full rounded-full bg-cyan-300 transition-all" style={{ width: `${widthPercent}%` }} />
      </div>
      {detailLabel ? <p className="text-xs text-slate-500">{detailLabel}</p> : null}
    </div>
  );
}

function CloudflareStatusBadge({ status }: { status: "configured" | "missing_config" | "error" }) {
  const labels = {
    configured: "Configured",
    missing_config: "Missing config",
    error: "Error",
  };
  const tone =
    status === "configured"
      ? "bg-emerald-400/10 text-emerald-200"
      : status === "missing_config"
        ? "bg-amber-400/10 text-amber-200"
        : "bg-rose-400/10 text-rose-200";
  return <span className={["rounded-full px-2.5 py-1 text-xs font-semibold", tone].join(" ")}>{labels[status]}</span>;
}

function ConfigStatusLine({ label, configured }: { label: string; configured: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-3 text-sm last:border-b-0 last:pb-0">
      <span className="text-slate-300">{label}</span>
      <span
        className={[
          "rounded-full px-2.5 py-1 text-xs font-semibold",
          configured ? "bg-emerald-400/10 text-emerald-200" : "bg-rose-400/10 text-rose-200",
        ].join(" ")}
      >
        {configured ? "Configured" : "Missing"}
      </span>
    </div>
  );
}

function KpiGrid({ metrics }: { metrics: AdminMetricsResponse }) {
  const cards: Array<{ label: string; value: string }> = [
    { label: "Συνολικές προσπάθειες", value: String(metrics.overview.totalAttempts) },
    { label: "Ολοκληρωμένα tests", value: String(metrics.overview.completedTests) },
    { label: "Completion rate", value: formatPercent(metrics.overview.completionRatePercent) },
    { label: "Avg time", value: formatDuration(metrics.overview.averageCompletionTimeMs) },
    { label: "Avg accuracy", value: formatPercent(metrics.overview.averageAccuracyPercent) },
    { label: "Started today", value: String(metrics.overview.startedToday) },
    { label: "Completed today", value: String(metrics.overview.completedToday) },
    { label: "Paid reports", value: String(metrics.sales.paidReports) },
    { label: "Gross revenue", value: metrics.sales.grossRevenueFormatted },
    { label: "Pending orders", value: String(metrics.sales.pendingOrders) },
    { label: "Paid conversion", value: formatPercent(metrics.sales.paidConversionRatePercent) },
    { label: "Contact messages", value: String(metrics.contact.contactMessageCount) },
    { label: "Reminder signups", value: String(metrics.contact.reminderSignupCount) },
    { label: "Refunds", value: metrics.sales.refunds },
  ];

  return (
    <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
      {cards.map((card) => (
        <AdminMetricCard key={card.label} label={card.label} value={card.value} />
      ))}
    </div>
  );
}

function SectionHeader({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <Card className="border-white/10 bg-slate-950/55">
      <p className="text-sm font-semibold uppercase tracking-[0.16em] text-cyan-200">{eyebrow}</p>
      <h2 className="mt-2 text-2xl font-bold text-white">{title}</h2>
      <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">{description}</p>
    </Card>
  );
}

function InfoCard({ children }: { children: ReactNode }) {
  return (
    <Card className="border-cyan-300/15 bg-cyan-300/[0.045]">
      <p className="text-sm leading-6 text-slate-300">{children}</p>
    </Card>
  );
}

function AdminSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="grid min-w-0 gap-2 text-sm font-semibold text-slate-200">
      {label}
      <select
        className="min-h-11 rounded-xl border border-white/10 bg-slate-950/75 px-3 py-2 text-sm text-white outline-none transition focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/20"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function RecentAttemptsTable({ attempts }: { attempts: AdminRecentAttempt[] }) {
  return (
    <TableCard title="Πρόσφατες προσπάθειες" emptyMessage="No attempts yet." isEmpty={attempts.length === 0}>
      <thead>
        <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.12em] text-slate-400">
          <th className="px-4 py-3 font-semibold">Attempt</th>
          <th className="px-4 py-3 font-semibold">Started</th>
          <th className="px-4 py-3 font-semibold">Age</th>
          <th className="px-4 py-3 font-semibold">Device</th>
          <th className="px-4 py-3 font-semibold">Status</th>
          <th className="px-4 py-3 font-semibold">Progress</th>
          <th className="px-4 py-3 font-semibold">Time</th>
          <th className="px-4 py-3 font-semibold">Accuracy</th>
          <th className="px-4 py-3 font-semibold">Band</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-white/10">
        {attempts.map((attempt) => (
          <tr key={attempt.attemptId} className="text-sm text-slate-200">
            <td className="whitespace-nowrap px-4 py-3 font-semibold text-cyan-100">{attempt.shortId}</td>
            <td className="whitespace-nowrap px-4 py-3">{formatDate(attempt.startedAt ?? attempt.createdAt)}</td>
            <td className="whitespace-nowrap px-4 py-3">{attempt.ageBand ?? "Unknown"}</td>
            <td className="whitespace-nowrap px-4 py-3">{statusLabel(attempt.status)}</td>
            <td className="whitespace-nowrap px-4 py-3">{attempt.progressLabel}</td>
            <td className="whitespace-nowrap px-4 py-3">{formatDuration(attempt.totalTimeMs)}</td>
            <td className="whitespace-nowrap px-4 py-3">{formatPercent(attempt.accuracyPercent)}</td>
            <td className="whitespace-nowrap px-4 py-3">{attempt.performanceBand ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </TableCard>
  );
}

function AttemptsTable({
  attempts,
  loading,
  reportPreviewLoadingId,
  labelSavingId,
  onSaveLabel,
  onViewReport,
}: {
  attempts: AdminAttemptListItem[];
  loading: boolean;
  reportPreviewLoadingId: string | null;
  labelSavingId: string | null;
  onSaveLabel: (attemptId: string, label: string) => Promise<void>;
  onViewReport: (attempt: AdminAttemptListItem) => void;
}) {
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [labelInput, setLabelInput] = useState("");

  function startEditingLabel(attempt: AdminAttemptListItem) {
    setEditingLabelId(attempt.attemptId);
    setLabelInput(attempt.adminLabel ?? "");
  }

  async function saveEditingLabel(attemptId: string) {
    try {
      await onSaveLabel(attemptId, labelInput);
      setEditingLabelId(null);
    } catch {
      // Parent section displays the save error.
    }
  }

  return (
    <TableCard title="Attempts" emptyMessage="No attempts match these filters." isEmpty={attempts.length === 0}>
      <thead>
        <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.12em] text-slate-400">
          <th className="px-4 py-3 font-semibold">Attempt</th>
          <th className="px-4 py-3 font-semibold">Created / Started</th>
          <th className="px-4 py-3 font-semibold">Completed</th>
          <th className="px-4 py-3 font-semibold">Age</th>
          <th className="px-4 py-3 font-semibold">Device</th>
          <th className="px-4 py-3 font-semibold">Status</th>
          <th className="px-4 py-3 font-semibold">Progress</th>
          <th className="px-4 py-3 font-semibold">Time</th>
          <th className="px-4 py-3 font-semibold">Accuracy</th>
          <th className="px-4 py-3 font-semibold">Paid report</th>
          <th className="px-4 py-3 font-semibold">Email</th>
          <th className="px-4 py-3 font-semibold">Report</th>
          <th className="px-4 py-3 font-semibold">Actions</th>
        </tr>
      </thead>
      <tbody className={["divide-y divide-white/10", loading ? "opacity-60" : ""].join(" ")}>
        {attempts.map((attempt) => (
          <tr key={attempt.attemptId} className="text-sm text-slate-200">
            <td className="min-w-56 px-4 py-3">
              <span className="block font-semibold text-cyan-100">{attempt.shortId}</span>
              {editingLabelId === attempt.attemptId ? (
                <div className="mt-2 flex flex-col gap-2">
                  <Input
                    value={labelInput}
                    maxLength={80}
                    placeholder="Admin label"
                    onChange={(event) => setLabelInput(event.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="rounded-lg border border-cyan-300/25 px-2.5 py-1 text-xs font-semibold text-cyan-100 transition hover:bg-cyan-300/10 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={labelSavingId === attempt.attemptId}
                      onClick={() => {
                        void saveEditingLabel(attempt.attemptId);
                      }}
                    >
                      {labelSavingId === attempt.attemptId ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-white/10 px-2.5 py-1 text-xs font-semibold text-slate-300 transition hover:bg-white/8"
                      disabled={labelSavingId === attempt.attemptId}
                      onClick={() => setEditingLabelId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-1 flex items-center gap-2">
                  <span className={["text-xs", attempt.adminLabel ? "text-slate-300" : "text-slate-500"].join(" ")}>
                    {attempt.adminLabel || "No label"}
                  </span>
                  <button
                    type="button"
                    className="rounded-md border border-white/10 px-1.5 py-0.5 text-[11px] font-semibold text-cyan-100 transition hover:bg-white/8"
                    onClick={() => startEditingLabel(attempt)}
                  >
                    Edit
                  </button>
                </div>
              )}
            </td>
            <td className="whitespace-nowrap px-4 py-3">
              <span className="block">{formatDate(attempt.createdAt)}</span>
              <span className="block text-xs text-slate-500">{formatDate(attempt.startedAt)}</span>
            </td>
            <td className="whitespace-nowrap px-4 py-3">{formatDate(attempt.completedAt)}</td>
            <td className="whitespace-nowrap px-4 py-3">{attempt.ageBand ?? "Unknown"}</td>
            <td className="whitespace-nowrap px-4 py-3">
              <DeviceDisplay type={attempt.deviceType} summary={attempt.deviceSummary} />
            </td>
            <td className="whitespace-nowrap px-4 py-3">{statusLabel(attempt.status)}</td>
            <td className="whitespace-nowrap px-4 py-3">{attempt.progressLabel}</td>
            <td className="whitespace-nowrap px-4 py-3">{formatDuration(attempt.totalTimeMs)}</td>
            <td className="whitespace-nowrap px-4 py-3">{formatPercent(attempt.accuracyPercent)}</td>
            <td className="whitespace-nowrap px-4 py-3">
              <span
                className={[
                  "rounded-full px-2.5 py-1 text-xs font-semibold",
                  attempt.paidReport ? "bg-emerald-400/15 text-emerald-200" : "bg-white/10 text-slate-300",
                ].join(" ")}
              >
                {attempt.paidReport ? "Paid" : "Unpaid"}
              </span>
              {attempt.orderStatus ? <span className="ml-2 text-xs text-slate-500">{orderStatusLabel(attempt.orderStatus)}</span> : null}
            </td>
            <td className="whitespace-nowrap px-4 py-3">{attempt.email ?? "—"}</td>
            <td className="whitespace-nowrap px-4 py-3">
              <span className="block">{attempt.reportId ?? "—"}</span>
              {attempt.reportStatus ? <span className="block text-xs text-slate-500">{attempt.reportStatus}</span> : null}
            </td>
            <td className="whitespace-nowrap px-4 py-3">
              {attempt.status === "completed" ? (
                <button
                  type="button"
                  className="rounded-lg border border-cyan-300/25 px-2.5 py-1.5 text-xs font-semibold text-cyan-100 transition hover:bg-cyan-300/10 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={reportPreviewLoadingId === attempt.attemptId}
                  onClick={() => onViewReport(attempt)}
                >
                  {reportPreviewLoadingId === attempt.attemptId ? "Loading..." : "View report"}
                </button>
              ) : (
                <span className="text-xs text-slate-500">—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </TableCard>
  );
}

function ReportPreviewModal({
  preview,
  onClose,
}: {
  preview: AdminReportPreviewState;
  onClose: () => void;
}) {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  async function handleDownloadPdf() {
    const token = sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
    if (!token) return;

    setPdfLoading(true);
    setPdfError(null);
    try {
      const pdf = await fetchAdminReportPreviewPdf(token, preview.attemptId);
      const url = URL.createObjectURL(pdf);
      const link = document.createElement("a");
      link.href = url;
      link.download = safeAdminPdfFilename(preview.report.reportId || preview.attemptId);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setPdfError("Could not generate the PDF right now.");
    } finally {
      setPdfLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/85 p-3 backdrop-blur-sm sm:p-6" role="dialog" aria-modal="true">
      <div className="mx-auto flex h-full max-w-7xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-slate-950 shadow-2xl">
        <div className="flex flex-col gap-3 border-b border-white/10 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200">Premium Report Preview</p>
            <h3 className="mt-1 text-xl font-bold text-white">Attempt {preview.attemptShortId}</h3>
            <p className="mt-1 text-sm text-slate-400">
              {preview.source === "stored" ? "Stored report" : "Generated preview"}
            </p>
            {pdfError ? <p className="mt-2 text-sm font-semibold text-rose-300">{pdfError}</p> : null}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button type="button" onClick={handleDownloadPdf} disabled={pdfLoading}>
              {pdfLoading ? "Preparing PDF..." : "Download PDF"}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ReportView report={preview.report} showPrintButton={false} />
        </div>
      </div>
    </div>
  );
}

function RecentOrdersTable({ orders }: { orders: AdminRecentOrder[] }) {
  return (
    <TableCard title="Recent orders" emptyMessage="No paid orders yet." isEmpty={orders.length === 0}>
      <thead>
        <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.12em] text-slate-400">
          <th className="px-4 py-3 font-semibold">Order</th>
          <th className="px-4 py-3 font-semibold">Date</th>
          <th className="px-4 py-3 font-semibold">Attempt</th>
          <th className="px-4 py-3 font-semibold">Email</th>
          <th className="px-4 py-3 font-semibold">Status</th>
          <th className="px-4 py-3 font-semibold">Amount</th>
          <th className="px-4 py-3 font-semibold">Stripe session</th>
          <th className="px-4 py-3 font-semibold">Report</th>
          <th className="px-4 py-3 font-semibold">Report status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-white/10">
        {orders.map((order) => (
          <tr key={order.orderId} className="text-sm text-slate-200">
            <td className="whitespace-nowrap px-4 py-3 font-semibold text-cyan-100">{order.shortId}</td>
            <td className="whitespace-nowrap px-4 py-3">{formatDate(order.paidAt ?? order.createdAt)}</td>
            <td className="whitespace-nowrap px-4 py-3">{order.attemptShortId ?? "—"}</td>
            <td className="whitespace-nowrap px-4 py-3">{order.email ?? "—"}</td>
            <td className="whitespace-nowrap px-4 py-3">{orderStatusLabel(order.status)}</td>
            <td className="whitespace-nowrap px-4 py-3">{order.amountFormatted}</td>
            <td className="whitespace-nowrap px-4 py-3">{order.stripeSessionShortId ?? "—"}</td>
            <td className="whitespace-nowrap px-4 py-3">{order.reportId ?? "—"}</td>
            <td className="whitespace-nowrap px-4 py-3">{order.reportStatus ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </TableCard>
  );
}

function OrdersTable({ orders, loading }: { orders: AdminOrderListItem[]; loading: boolean }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <TableCard title="Order groups" emptyMessage="No orders match these filters." isEmpty={orders.length === 0}>
      <thead>
        <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.12em] text-slate-400">
          <th className="px-4 py-3 font-semibold">Attempt</th>
          <th className="px-4 py-3 font-semibold">Latest checkout</th>
          <th className="px-4 py-3 font-semibold">Checkout device</th>
          <th className="px-4 py-3 font-semibold">Email</th>
          <th className="px-4 py-3 font-semibold">Group status</th>
          <th className="px-4 py-3 font-semibold">Attempts</th>
          <th className="px-4 py-3 font-semibold">Pending / Paid</th>
          <th className="px-4 py-3 font-semibold">Amount</th>
          <th className="px-4 py-3 font-semibold">Report</th>
          <th className="px-4 py-3 font-semibold">Email delivery</th>
          <th className="px-4 py-3 font-semibold">Latest session</th>
          <th className="px-4 py-3 font-semibold">Details</th>
        </tr>
      </thead>
      <tbody className={["divide-y divide-white/10", loading ? "opacity-60" : ""].join(" ")}>
        {orders.map((orderGroup) => {
          const isExpanded = expandedId === orderGroup.attemptId;
          return (
            <Fragment key={orderGroup.attemptId}>
              <tr className="text-sm text-slate-200">
                <td className="whitespace-nowrap px-4 py-3 font-semibold text-cyan-100">{orderGroup.attemptShortId}</td>
                <td className="whitespace-nowrap px-4 py-3">{formatDate(orderGroup.latestOrderAt)}</td>
                <td className="whitespace-nowrap px-4 py-3">
                  <DeviceDisplay type={orderGroup.checkoutDeviceType} summary={orderGroup.checkoutDeviceSummary} />
                </td>
                <td className="whitespace-nowrap px-4 py-3">{orderGroup.email ?? "—"}</td>
                <td className="whitespace-nowrap px-4 py-3">{orderStatusLabel(orderGroup.groupStatus)}</td>
                <td className="whitespace-nowrap px-4 py-3">{orderGroup.paymentAttempts}</td>
                <td className="whitespace-nowrap px-4 py-3">
                  {orderGroup.pendingAttempts} / {orderGroup.paidAttempts}
                </td>
                <td className="whitespace-nowrap px-4 py-3">{orderGroup.paidAmountFormatted ?? "—"}</td>
                <td className="whitespace-nowrap px-4 py-3">
                  <span className="block">{orderGroup.reportId ?? "—"}</span>
                  {orderGroup.reportStatus ? <span className="block text-xs text-slate-500">{orderGroup.reportStatus}</span> : null}
                </td>
                <td className="whitespace-nowrap px-4 py-3">{emailStatusLabel(orderGroup.emailStatus)}</td>
                <td className="whitespace-nowrap px-4 py-3">{orderGroup.latestStripeSessionShortId ?? "—"}</td>
                <td className="whitespace-nowrap px-4 py-3">
                  <button
                    type="button"
                    className="rounded-lg border border-white/10 px-2 py-1 text-xs font-semibold text-cyan-100 transition hover:bg-white/8"
                    onClick={() => setExpandedId(isExpanded ? null : orderGroup.attemptId)}
                  >
                    {isExpanded ? "Hide" : "Show"} ({orderGroup.orders.length})
                  </button>
                </td>
              </tr>
              {isExpanded ? (
                <tr className="bg-white/[0.025] text-sm text-slate-300">
                  <td className="px-4 py-4" colSpan={12}>
                    <div className="mb-3 flex flex-wrap gap-3 text-xs text-slate-400">
                      <span>First checkout: {formatDate(orderGroup.firstOrderAt)}</span>
                      <span>Failed: {orderGroup.failedAttempts}</span>
                      <span>Expired: {orderGroup.expiredAttempts}</span>
                      <span>Report opens: {orderGroup.reportOpenCount}</span>
                      <span>PDF downloads: {orderGroup.pdfDownloadCount}</span>
                    </div>
                    <div className="overflow-x-auto rounded-xl border border-white/10">
                      <table className="w-full min-w-[720px]">
                        <thead>
                          <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.12em] text-slate-500">
                            <th className="px-3 py-2 font-semibold">Order</th>
                            <th className="px-3 py-2 font-semibold">Created</th>
                            <th className="px-3 py-2 font-semibold">Paid</th>
                            <th className="px-3 py-2 font-semibold">Status</th>
                            <th className="px-3 py-2 font-semibold">Amount</th>
                            <th className="px-3 py-2 font-semibold">Checkout device</th>
                            <th className="px-3 py-2 font-semibold">Stripe session</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/10">
                          {orderGroup.orders.map((child) => (
                            <tr key={child.orderId}>
                              <td className="whitespace-nowrap px-3 py-2 font-semibold text-cyan-100">{child.shortId}</td>
                              <td className="whitespace-nowrap px-3 py-2">{formatDate(child.createdAt)}</td>
                              <td className="whitespace-nowrap px-3 py-2">{formatDate(child.paidAt)}</td>
                              <td className="whitespace-nowrap px-3 py-2">{orderStatusLabel(child.status)}</td>
                              <td className="whitespace-nowrap px-3 py-2">{child.amountFormatted}</td>
                              <td className="whitespace-nowrap px-3 py-2">
                                <DeviceDisplay type={child.checkoutDeviceType} summary={child.checkoutDeviceSummary} />
                              </td>
                              <td className="whitespace-nowrap px-3 py-2">{child.stripeSessionShortId ?? "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              ) : null}
            </Fragment>
          );
        })}
      </tbody>
    </TableCard>
  );
}

function LegacyOrdersTable({
  orders,
  loading,
}: {
  orders: Array<
    AdminOrderChildItem & {
      email?: string | null;
      attemptShortId?: string;
      reportId?: string | null;
      reportStatus?: string | null;
      emailStatus?: string | null;
    }
  >;
  loading: boolean;
}) {
  return (
    <TableCard title="Orders" emptyMessage="No orders match these filters." isEmpty={orders.length === 0}>
      <thead>
        <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.12em] text-slate-400">
          <th className="px-4 py-3 font-semibold">Order</th>
          <th className="px-4 py-3 font-semibold">Created</th>
          <th className="px-4 py-3 font-semibold">Paid</th>
          <th className="px-4 py-3 font-semibold">Email</th>
          <th className="px-4 py-3 font-semibold">Status</th>
          <th className="px-4 py-3 font-semibold">Amount</th>
          <th className="px-4 py-3 font-semibold">Attempt</th>
          <th className="px-4 py-3 font-semibold">Report</th>
          <th className="px-4 py-3 font-semibold">Report status</th>
          <th className="px-4 py-3 font-semibold">Email delivery</th>
          <th className="px-4 py-3 font-semibold">Stripe session</th>
        </tr>
      </thead>
      <tbody className={["divide-y divide-white/10", loading ? "opacity-60" : ""].join(" ")}>
        {orders.map((order) => (
          <tr key={order.orderId} className="text-sm text-slate-200">
            <td className="whitespace-nowrap px-4 py-3 font-semibold text-cyan-100">{order.shortId}</td>
            <td className="whitespace-nowrap px-4 py-3">{formatDate(order.createdAt)}</td>
            <td className="whitespace-nowrap px-4 py-3">{formatDate(order.paidAt)}</td>
            <td className="whitespace-nowrap px-4 py-3">{order.email ?? "—"}</td>
            <td className="whitespace-nowrap px-4 py-3">{orderStatusLabel(order.status)}</td>
            <td className="whitespace-nowrap px-4 py-3">{order.amountFormatted}</td>
            <td className="whitespace-nowrap px-4 py-3">{order.attemptShortId}</td>
            <td className="whitespace-nowrap px-4 py-3">{order.reportId ?? "—"}</td>
            <td className="whitespace-nowrap px-4 py-3">{order.reportStatus ?? "—"}</td>
            <td className="whitespace-nowrap px-4 py-3">{emailStatusLabel(order.emailStatus ?? null)}</td>
            <td className="whitespace-nowrap px-4 py-3">{order.stripeSessionShortId ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </TableCard>
  );
}

function RecentContactTable({ entries }: { entries: AdminContactEntry[] }) {
  return (
    <TableCard title="Recent emails / contact" emptyMessage="No contact entries yet." isEmpty={entries.length === 0}>
      <thead>
        <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.12em] text-slate-400">
          <th className="px-4 py-3 font-semibold">Date</th>
          <th className="px-4 py-3 font-semibold">Type</th>
          <th className="px-4 py-3 font-semibold">Email</th>
          <th className="px-4 py-3 font-semibold">Subject</th>
          <th className="px-4 py-3 font-semibold">Preview</th>
          <th className="px-4 py-3 font-semibold">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-white/10">
        {entries.map((entry) => (
          <tr key={entry.id} className="text-sm text-slate-200">
            <td className="whitespace-nowrap px-4 py-3">{formatDate(entry.createdAt)}</td>
            <td className="whitespace-nowrap px-4 py-3">{contactKindLabel(entry.kind)}</td>
            <td className="whitespace-nowrap px-4 py-3">{entry.email}</td>
            <td className="min-w-44 px-4 py-3">{entry.subject}</td>
            <td className="min-w-64 px-4 py-3 text-slate-300">{entry.messagePreview}</td>
            <td className="whitespace-nowrap px-4 py-3">{entry.status}</td>
          </tr>
        ))}
      </tbody>
    </TableCard>
  );
}

function EmailsTable({
  emails,
  loading,
  onSendReportEmail,
}: {
  emails: AdminEmailListItem[];
  loading: boolean;
  onSendReportEmail: (email: AdminEmailListItem) => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <TableCard title="Email records" emptyMessage="No email records match these filters." isEmpty={emails.length === 0}>
      <thead>
        <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.12em] text-slate-400">
          <th className="px-4 py-3 font-semibold">Date</th>
          <th className="px-4 py-3 font-semibold">Type</th>
          <th className="px-4 py-3 font-semibold">Email</th>
          <th className="px-4 py-3 font-semibold">Subject / Source</th>
          <th className="px-4 py-3 font-semibold">Status</th>
          <th className="px-4 py-3 font-semibold">Paid</th>
          <th className="px-4 py-3 font-semibold">Preview</th>
          <th className="px-4 py-3 font-semibold">Provider / message ID</th>
          <th className="px-4 py-3 font-semibold">Actions</th>
        </tr>
      </thead>
      <tbody className={["divide-y divide-white/10", loading ? "opacity-60" : ""].join(" ")}>
        {emails.map((email) => {
          const canExpand = Boolean(email.fullMessage);
          const isExpanded = expandedId === email.id;
          return (
            <Fragment key={email.id}>
              <tr className="text-sm text-slate-200">
                <td className="whitespace-nowrap px-4 py-3">{formatDate(email.createdAt)}</td>
                <td className="whitespace-nowrap px-4 py-3">{emailTypeLabel(email.type)}</td>
                <td className="whitespace-nowrap px-4 py-3">
                  {email.name ? <span className="block font-semibold text-white">{email.name}</span> : null}
                  <span className={email.name ? "block text-xs text-slate-400" : ""}>{email.email}</span>
                </td>
                <td className="min-w-52 px-4 py-3">
                  <span className="block">{email.subject ?? "—"}</span>
                  {email.source ? <span className="block text-xs text-slate-500">{email.source}</span> : null}
                </td>
                <td className="whitespace-nowrap px-4 py-3">{emailStatusLabel(email.status)}</td>
                <td className="whitespace-nowrap px-4 py-3">{paidCustomerLabel(email.paidCustomer)}</td>
                <td className="min-w-72 px-4 py-3 text-slate-300">
                  <span>{email.preview}</span>
                  {canExpand ? (
                    <button
                      type="button"
                      className="ml-3 rounded-lg border border-white/10 px-2 py-1 text-xs font-semibold text-cyan-100 transition hover:bg-white/8"
                      onClick={() => setExpandedId(isExpanded ? null : email.id)}
                    >
                      {isExpanded ? "Hide" : "Details"}
                    </button>
                  ) : null}
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  <span className="block">{email.provider ?? "—"}</span>
                  {email.providerMessageId ? <span className="block text-xs text-slate-500">{email.providerMessageId}</span> : null}
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  {email.type === "contact" ? (
                    <button
                      type="button"
                      className="rounded-lg border border-cyan-300/25 px-2.5 py-1.5 text-xs font-semibold text-cyan-100 transition hover:bg-cyan-300/10"
                      onClick={() => onSendReportEmail(email)}
                    >
                      Send report email
                    </button>
                  ) : (
                    <span className="text-xs text-slate-500">—</span>
                  )}
                </td>
              </tr>
              {isExpanded ? (
                <tr className="bg-white/[0.025] text-sm text-slate-300">
                  <td className="px-4 py-4" colSpan={9}>
                    <p className="mb-3 text-sm text-slate-400">
                      From: {email.name ? `${email.name} <${email.email}>` : email.email}
                    </p>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-cyan-200">Full message</p>
                    <p className="whitespace-pre-wrap leading-6">{email.fullMessage}</p>
                  </td>
                </tr>
              ) : null}
            </Fragment>
          );
        })}
      </tbody>
    </TableCard>
  );
}

function TableCard({
  title,
  emptyMessage,
  isEmpty,
  children,
}: {
  title: string;
  emptyMessage: string;
  isEmpty: boolean;
  children: ReactNode;
}) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b border-white/10 px-5 py-4">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
      </div>
      {isEmpty ? (
        <p className="px-5 py-6 text-sm text-slate-400">{emptyMessage}</p>
      ) : (
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[940px]">{children}</table>
        </div>
      )}
    </Card>
  );
}

function BreakdownList({
  title,
  items,
  labelKey,
  emptyLabel = "Unknown",
}: {
  title: string;
  items: Array<Record<string, string | number | null>>;
  labelKey: string;
  emptyLabel?: string;
}) {
  return (
    <Card>
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      {items.length === 0 ? (
        <p className="mt-4 text-sm text-slate-400">No data yet.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {items.map((item) => {
            const rawLabel = item[labelKey];
            const label = rawLabel === null || rawLabel === "" ? emptyLabel : String(rawLabel);
            return (
              <div key={`${title}-${label}`} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-slate-300">{label}</span>
                <span className="rounded-full bg-white/10 px-2.5 py-1 font-semibold text-cyan-100">{item.count}</span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function formatPercent(value: number | null) {
  if (value === null) {
    return "—";
  }
  return `${value}%`;
}

function formatDuration(value: number | null) {
  if (value === null) {
    return "—";
  }

  const totalSeconds = Math.round(value / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
}

function formatDate(value: string | null | undefined) {
  if (!value) {
    return "—";
  }

  return new Intl.DateTimeFormat("el-GR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatBytes(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size >= 10 || unitIndex === 0 ? Math.round(size) : size.toFixed(1)} ${units[unitIndex]}`;
}

function formatUsagePercent(value: number) {
  if (!Number.isFinite(value)) return "0%";
  if (value > 0 && value < 0.01) return "<0.01%";
  if (value < 1) return `${Math.round(value * 1000) / 1000}%`;
  if (value < 10) return `${Math.round(value * 10) / 10}%`;
  return `${Math.round(value)}%`;
}

function statusLabel(status: string) {
  const labels: Record<string, string> = {
    started: "Started",
    completed: "Completed",
    abandoned: "Abandoned",
    expired: "Expired",
  };
  return labels[status] ?? status;
}

function orderStatusLabel(status: string) {
  const labels: Record<string, string> = {
    pending: "Pending",
    paid: "Paid",
    failed: "Failed",
    expired: "Expired",
  };
  return labels[status] ?? status;
}

function emailStatusLabel(status: string | null) {
  if (!status) return "Not sent";
  const labels: Record<string, string> = {
    new: "New",
    sent: "Sent",
    failed: "Failed",
    skipped: "Skipped",
  };
  return labels[status] ?? status;
}

function emailTypeLabel(type: AdminEmailType) {
  const labels: Record<AdminEmailType, string> = {
    contact: "Contact",
    progress_reminder: "Progress reminder",
    report_delivery: "Report delivery",
    unknown: "Unknown",
  };
  return labels[type];
}

function paidCustomerLabel(value: boolean | null) {
  if (value === null) return "Unknown";
  return value ? "Paid" : "Non-paid";
}

function contactKindLabel(kind: AdminContactEntry["kind"]) {
  const labels: Record<AdminContactEntry["kind"], string> = {
    contact: "Contact",
    progress_reminder: "Progress reminder",
    unknown: "Unknown",
  };
  return labels[kind];
}
